/**
 * LinkHubNetworkTab.tsx v6.3.0 - INTEGRADO CON IRC REAL
 * @version 6.3.0
 * @date 2025-01-19
 * 
 * IRC Communities Manager con mIRC32 completo + datos reales
 * 
 * Changelog v6.3.0:
 * - ✅ Precarga silenciosa de canales, stats y usuarios al conectar
 * - ✅ No espera cambio de tab para cargar datos
 * - ✅ Datos disponibles en todo el componente desde el inicio
 * - ✅ Support para mensajes privados (isPrivate flag)
 * 
 * Changelog v6.2.0:
 * - Fix race conditions en auto-refresh con flag isActive
 * - Retry logic en carga inicial de usuarios (3 intentos)
 * - Delay aumentado a 1.5s en evento USERLIST
 * - Mejor tracking de fetches con IDs únicos
 * - Cleanup mejorado al cambiar de canal
 */

import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Badge, Form, InputGroup, Table, Pagination, ButtonGroup, Alert, Nav, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers, faHashtag, faPlus, faCircle, faSearch, faCrown, faUserShield,
  faClock, faComments, faFire, faEye, faBan, faTimes, faMoon, faSun, faPalette,
  faMessage, faEllipsisV, faServer, faCheck, faExclamationTriangle, faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { useWindowManager } from '../../hooks/useWindowManager';
import linkHubIrcService, { IRCMessage, IRCConfig, IRCChannel, IRCStats } from '../../service/linkHubIrcService';
import mirclogo from '../../assets/img/Anubis/mirc32_1.png';
import { channel } from 'diagnostics_channel';
import { render } from 'katex';
import '../../types/LinkHubNetworkTab.css';

interface Community {
  id: string;
  name: string;
  topic: string;
  users: number;
  messages: number;
  lastMessage: { user: string; text: string; time: string };
  activity: number[];
  status: 'active' | 'quiet' | 'hot';
  // ✅ NUEVO: Campos para modo RRSS
  avatar?: string;           // URL de imagen de avatar/logo
  banner?: string;           // URL de imagen de banner/portada
  description?: string;      // Descripción rica del grupo
  category?: string;         // Categoría (Tecnología, Gaming, etc)
  verified?: boolean;        // Badge de verificado
  trending?: boolean;        // Trending topic
  tags?: string[];          // Tags/hashtags
  externalLinks?: {         // Links a otras RRSS (preparado para APIs)
    twitter?: string;
    discord?: string;
    telegram?: string;
    website?: string;
  };
}

interface User {
  id: string;
  nick: string;
  host: string;
  privilege: 'owner' | 'op' | 'voice' | 'user';
  channels: string[];
  lastSeen: string;
  messages: number;
}

interface Channel {
  id: string;
  name: string;
  topic: string;
  users: number;
  messages: number;
  mode: string;
  created: string;
}

interface IRCUser {
  nick: string;
  host: string;
  privilege: 'owner' | 'op' | 'voice' | 'user';
  idle: number;
}

const LinkHubNetworkTab: React.FC = () => {
  const [activeSection, setActiveSection] = useState('communities');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [channelCardsCount, setChannelCardsCount] = useState(6);
  const [commandInput, setCommandInput] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [skillMessage, setSkillMessage] = useState('');
  
  const windowManager = useWindowManager();
  
  const [mirc32Unlocked, setMirc32Unlocked] = useState(
    localStorage.getItem('skill_mirc32') === 'true'
  );
  const [themesUnlocked, setThemesUnlocked] = useState(
    localStorage.getItem('skill_themes') === 'true'
  );
  const [mircDarkMode, setMircDarkMode] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState('classic');
  const [mircActiveChannel, setMircActiveChannel] = useState('#general');
  const [channelPanelWidth, setChannelPanelWidth] = useState(200);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; user: IRCUser } | null>(null);
  
  const [showIrcConfig, setShowIrcConfig] = useState(false);
  const [ircConnected, setIrcConnected] = useState(false);
  const [ircMessages, setIrcMessages] = useState<Map<string, IRCMessage[]>>(new Map());
  const [connectionLogs, setConnectionLogs] = useState<string[]>([]);
  const [ircChannels, setIrcChannels] = useState<IRCChannel[]>([]);
  const [ircUsers, setIrcUsers] = useState<Map<string, IRCUser[]>>(new Map());
  const [ircStats, setIrcStats] = useState<IRCStats | null>(null);
  const [ircConfig, setIrcConfig] = useState<IRCConfig>({
    server: 'irc.linuxcorp.net',
    port: 6697,
    nickname: 'LinkHubUserFront00',
    channels: ['#linkhub'],
    useSSL: true,
    password: '',
    alternativeNicks: ['LinkHubUserFront01', 'LinkHubUserFront02']
  });
  
  // ✅ NUEVO: Tracking de última actividad por canal
  const [lastActivity, setLastActivity] = useState<Map<string, number>>(new Map());
  // ✅ NUEVO: Historial de mensajes por hora para gráfico (últimas 7 horas)
  const [messageHistory, setMessageHistory] = useState<Map<string, number[]>>(new Map());
  
  // ✅ NUEVO: Custom avatars por canal (localStorage)
  const [customAvatars, setCustomAvatars] = useState<Map<string, string>>(() => {
    const stored = localStorage.getItem('linkhub_custom_avatars');
    return stored ? new Map(JSON.parse(stored)) : new Map();
  });
  
  const listenersAttachedRef = React.useRef(false);
  
  // ✅ NUEVO: Refs para auto-scroll silencioso en mIRC32
  const mircChatRef = useRef<HTMLDivElement>(null);
  const mircMessagesEndRef = useRef<HTMLDivElement>(null);
  const mircContainerRef = useRef<HTMLDivElement>(null); // ✅ Ref para límites del menú contextual
  const mircUserScrolledUp = useRef(false);  // Track si el usuario scrolleó hacia arriba
  const mircPrevMessageCount = useRef(0);    // Track cantidad de mensajes anterior
  const [mircHasNewMessages, setMircHasNewMessages] = useState(false); // Indicador visual

  // ✅ NUEVO: Sistema de modales y notificaciones personalizadas
  const [showJoinChannelModal, setShowJoinChannelModal] = useState(false);
  const [joinChannelInput, setJoinChannelInput] = useState('');
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionModalConfig, setActionModalConfig] = useState<{
    title: string;
    label: string;
    defaultValue: string;
    placeholder: string;
    onConfirm: (value: string) => void;
  } | null>(null);
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
    details?: string;
  }>>([]);

    /**
 * ✅ NUEVO: Agregar feedback visual de comandos IRC
 */
const addCommandFeedback = React.useCallback((channel: string, text: string, type: 'sent' | 'result' | 'error') => {
  const commandMsg: IRCMessage = {
    id: `cmd_${Date.now()}_${Math.random()}`,
    type: 'COMMAND',
    channel: channel,
    from: 'System',
    text: text,
    timestamp: Date.now(),
    metadata: { 
      isCommandFeedback: true,
      commandType: type 
    }
  };
  
  setIrcMessages(prev => {
    const newMap = new Map(prev);
    const channelMsgs = newMap.get(channel) || [];
    newMap.set(channel, [...channelMsgs, commandMsg]);
    return newMap;
  });
}, []);

  // ✅ NUEVO: Sistema de notificaciones toast
  const showNotification = React.useCallback((
    type: 'success' | 'error' | 'warning' | 'info',
    message: string,
    details?: string
  ) => {
    const id = `notif_${Date.now()}_${Math.random()}`;
    setNotifications(prev => [...prev, { id, type, message, details }]);
    
    // Auto-dismiss después de 5 segundos
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  }, []);

  // ✅ NUEVO: Mostrar modal de acción genérico
  const showActionPrompt = React.useCallback((
    title: string,
    label: string,
    defaultValue: string,
    placeholder: string,
    onConfirm: (value: string) => void
  ) => {
    setActionModalConfig({ title, label, defaultValue, placeholder, onConfirm });
    setShowActionModal(true);
  }, []);

    let allUsers: User[] = [];
    
    if (ircConnected && ircUsers.size > 0) {
      // Convertir Map<channel, IRCUser[]> a User[]
      const usersMap = new Map<string, User>();
      
      for (const [channel, channelUsers] of ircUsers) {
        for (const ircUser of channelUsers) {
          const existingUser = usersMap.get(ircUser.nick);
          
          if (existingUser) {
            // Agregar canal si no está
            if (!existingUser.channels.includes(channel)) {
              existingUser.channels.push(channel);
            }
          } else {
            // Determinar privilegio basado en modes
            let privilege: 'owner' | 'op' | 'voice' | 'user' = 'user';
            if (ircUser.privilege === 'owner' || (ircUser as any).modes?.includes('q')) {
              privilege = 'owner';
            } else if (ircUser.privilege === 'op' || (ircUser as any).modes?.includes('o')) {
              privilege = 'op';
            } else if (ircUser.privilege === 'voice' || (ircUser as any).modes?.includes('v')) {
              privilege = 'voice';
            }
            
            usersMap.set(ircUser.nick, {
              id: ircUser.nick,
              nick: ircUser.nick,
              host: ircUser.host || 'unknown',
              privilege,
              channels: [channel],
              lastSeen: ircUser.idle ? `${Math.floor(ircUser.idle / 60)}m` : 'now',
              messages: 0 // TODO: Contar desde ircMessages
            });
          }
        }
      }
      
      allUsers = Array.from(usersMap.values());
    }
    
    const filteredUsers = allUsers.filter(u => 
      u.nick.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.host.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalUsers = filteredUsers.length;

  const addLog = (msg: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const logMsg = `[${timestamp}] ${msg}`;
    console.log(logMsg);
    setConnectionLogs(prev => [...prev, logMsg].slice(-50));
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // GESTIÓN DE AVATARES PERSONALIZADOS
  // ═══════════════════════════════════════════════════════════════════════════
  const updateCommunityAvatar = (channelName: string, imageUrl: string) => {
    setCustomAvatars(prev => {
      const newMap = new Map(prev);
      newMap.set(channelName, imageUrl);
      
      // Guardar en localStorage
      localStorage.setItem('linkhub_custom_avatars', JSON.stringify(Array.from(newMap.entries())));
      
      console.log(`✅ Avatar actualizado para ${channelName}`);
      return newMap;
    });
  };

  const handleEditAvatar = (channelName: string) => {
    const imageUrl = prompt(
      `🖼️ Avatar personalizado para ${channelName}\n\n` +
      `Ingresa la URL de la imagen:\n` +
      `(Puede ser desde imgur.com, i.ibb.co, o cualquier URL pública)`,
      customAvatars.get(channelName) || ''
    );
    
    if (imageUrl !== null) {
      if (imageUrl.trim() === '') {
        // Eliminar avatar personalizado
        setCustomAvatars(prev => {
          const newMap = new Map(prev);
          newMap.delete(channelName);
          localStorage.setItem('linkhub_custom_avatars', JSON.stringify(Array.from(newMap.entries())));
          return newMap;
        });
        console.log(`🗑️ Avatar personalizado eliminado para ${channelName}`);
      } else {
        updateCommunityAvatar(channelName, imageUrl.trim());
      }
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // OBTENER USUARIOS DE UN CANAL
  // ═══════════════════════════════════════════════════════════════════════════
  const fetchChannelUsers = React.useCallback(async (channel: string) => {
    try {
      addLog(`👥 Obteniendo usuarios de ${channel}...`);
      
      // Extraer userId del token
      const token = localStorage.getItem('hatateToken');
      let userId = 'unknown';
      
      if (token) {
        try {
          const tokenParts = token.split('.');
          if (tokenParts.length === 3) {
            const payload = JSON.parse(atob(tokenParts[1]));
            userId = payload._id || payload.userId || payload.id || 'unknown';
          }
        } catch (e) {
          console.error('Error extrayendo userId del token:', e);
        }
      }
      
      const response = await fetch(
        `https://backend.anubisai.net/api/linkhub/users/${encodeURIComponent(channel)}?userId=${userId}`,
        {
          headers: {
            'x-api-key': 'anubis-whatsapp-api-key',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Error ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.users) {
        const users: IRCUser[] = data.users.map((u: any) => ({
          nick: u.nick || u.nickname || u.name,
          host: u.host || u.hostname || 'unknown',
          privilege: u.privilege || u.mode || 'user',
          idle: u.idle || 0
        }));
        
        setIrcUsers(prev => {
          const newMap = new Map(prev);
          newMap.set(channel, users);
          return newMap;
        });
        
        addLog(`✅ ${users.length} usuarios cargados de ${channel}`);
      }
    } catch (error: any) {
      addLog(`❌ Error obteniendo usuarios de ${channel}: ${error.message}`);
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // REFRESCAR CANALES DESDE EL SERVICIO
  // ═══════════════════════════════════════════════════════════════════════════
  const refreshChannels = React.useCallback(async () => {
    try {
      const channels = await linkHubIrcService.getChannels();
      setIrcChannels(channels);
      addLog(`📺 Canales actualizados: ${channels.length} canales`);
      
      // Si hay canales y no hay canal activo, seleccionar el primero
      if (channels.length > 0 && !mircActiveChannel) {
        setMircActiveChannel(channels[0].name);
      }
    } catch (error: any) {
      addLog(`❌ Error al obtener canales: ${error.message}`);
    }
  }, [mircActiveChannel]);

  // ═══════════════════════════════════════════════════════════════════════════
  // REFRESCAR STATS DESDE EL SERVICIO
  // ═══════════════════════════════════════════════════════════════════════════
  const refreshStats = React.useCallback(async () => {
    try {
      const stats = await linkHubIrcService.getStats();
      if (stats) {
        setIrcStats(stats);
        addLog(`📊 Stats obtenidos: ${stats.total_users} usuarios`);
      }
    } catch (error: any) {
      addLog(`❌ Error al obtener stats: ${error.message}`);
    }
  }, []);

  const itemsPerPage = 10;
  const refreshChannelsRef = useRef(refreshChannels);
  const fetchChannelUsersRef = useRef(fetchChannelUsers);
  const mircActiveChannelRef = useRef(mircActiveChannel);

// ✅ Actualizar refs cuando cambien las funciones/valores
useEffect(() => {
  refreshChannelsRef.current = refreshChannels;
}, [refreshChannels]);

useEffect(() => {
  fetchChannelUsersRef.current = fetchChannelUsers;
}, [fetchChannelUsers]);

useEffect(() => {
  mircActiveChannelRef.current = mircActiveChannel;
}, [mircActiveChannel]);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIGURAR AUTENTICACIÓN AL MONTAR
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const token = localStorage.getItem('hatateToken');
    if (token) {
      linkHubIrcService.setAuth('anubis-whatsapp-api-key', `Bearer ${token}`);
      addLog('🔐 Autenticación configurada con hatateToken');
    } else {
      addLog('⚠️ No se encontró hatateToken en localStorage');
    }
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // SUSCRIBIRSE A EVENTOS IRC
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
  // ✅ Verificar si ya hay listeners antes de suscribir
  if (listenersAttachedRef.current) {
    console.log('⚠️ Listeners ya están suscritos, saltando...');
    return;
  }
  
  addLog('🎧 Configurando listeners de IRC...');
  listenersAttachedRef.current = true;
  
  // ✅ Guardar referencias a las funciones de unsub
  const unsubscribers: (() => void)[] = [];
  
  const unsubMessage = linkHubIrcService.onMessage((message: IRCMessage) => {
    addLog(`📨 Mensaje recibido: ${message.channel || 'server'} - ${message.from || 'system'}`);
    const channel = message.channel || 'server';
    
    // ✅ Usar callback form para evitar stale closures
    setIrcMessages(prev => {
      const newMap = new Map(prev);
      const channelMsgs = newMap.get(channel) || [];
      
      // ✅ PREVENIR DUPLICADOS - verificar que el mensaje no exista ya
      const messageExists = channelMsgs.some(msg => 
        msg.id === message.id || 
        (msg.timestamp === message.timestamp && msg.text === message.text && msg.from === message.from)
      );
      
      if (!messageExists) {
        newMap.set(channel, [...channelMsgs, message]);
      }
      
      return newMap;
    });
    
    // ✅ NUEVO: Actualizar última actividad del canal
    setLastActivity(prev => {
      const newMap = new Map(prev);
      newMap.set(channel, Date.now());
      return newMap;
    });
    
    // ✅ NUEVO: Actualizar historial de mensajes por hora
    setMessageHistory(prev => {
      const newMap = new Map(prev);
      const history = newMap.get(channel) || Array(7).fill(0);
      
      // Incrementar el contador de la hora actual (índice 6)
      const updated = [...history];
      updated[6] = (updated[6] || 0) + 1;
      
      newMap.set(channel, updated);
      return newMap;
    });
  });
  unsubscribers.push(unsubMessage);

  const unsubEvent = linkHubIrcService.onEvent((event: IRCMessage) => {
  addLog(`📢 Evento IRC: ${event.type} - ${event.text?.substring(0, 50)}`);
  
  // ✅ MODE: Cambios de privilegios (@op, +v, etc.)
  if (event.type === 'MODE' && event.channel && event.text) {
    addCommandFeedback(event.channel, `✓ ${event.text}`, 'result');
    
    // ✅ CRÍTICO: Refrescar usuarios del canal para actualizar privilegios
    if (event.channel.startsWith('#')) {
      addLog(`🔄 Refrescando usuarios de ${event.channel} por cambio MODE...`);
      setTimeout(() => {
        fetchChannelUsersRef.current(event.channel!);
      }, 500);
    }
  }
  
  // ✅ WHOIS: Mostrar respuesta en ventana activa
  if (event.type === 'WHOIS' && event.text) {
    addLog(`📋 WHOIS: ${event.text}`);
    
    // Obtener el nick del WHOIS desde metadata
    const whoisNick = event.metadata?.nick || 'unknown';
    
    // Buscar ventana activa (la última que trajo al frente)
    const activeWindow = windowManager.windows
      .sort((a, b) => b.zIndex - a.zIndex)[0];
    
    if (activeWindow?.metadata?.channelName) {
      const channelName = activeWindow.metadata.channelName;
      addCommandFeedback(channelName, `📋 ${event.text}`, 'result');
    } else {
      // Si no hay ventana activa, mostrar en consola
      console.log(`📋 WHOIS: ${event.text}`);
    }
  }
  
  // ✅ IRC_ERROR: Errores del servidor
  if (event.type === 'IRC_ERROR' && event.text) {
    addLog(`⚠️ IRC Error: ${event.text}`);
    
    // Buscar ventana activa
    const activeWindow = windowManager.windows
      .sort((a, b) => b.zIndex - a.zIndex)[0];
    
    if (activeWindow?.metadata?.channelName) {
      const channelName = activeWindow.metadata.channelName;
      addCommandFeedback(channelName, `❌ ${event.text}`, 'error');
    }
  }
  
  if (event.type === 'KICK' && event.channel && event.text) {
    addCommandFeedback(event.channel, `✓ ${event.text}`, 'result');
  }
  
  // ✅ JOIN/PART: Refrescar canales y usuarios
  if (event.type === 'JOIN' || event.type === 'PART') {
    setTimeout(() => {
      refreshChannelsRef.current();
    }, 300);
    
    if (event.channel === mircActiveChannelRef.current) {
      setTimeout(() => {
        fetchChannelUsersRef.current(event.channel!);
      }, 500);
    }
  }
  
  // ✅ QUIT: Refrescar usuarios de TODOS los canales
  if (event.type === 'QUIT') {
    addLog(`👤 Usuario desconectado, refrescando canales...`);
    setTimeout(() => {
      refreshChannelsRef.current();
      // Refrescar usuarios del canal activo
      if (mircActiveChannelRef.current) {
        fetchChannelUsersRef.current(mircActiveChannelRef.current);
      }
    }, 500);
  }
  
  // ✅ USERLIST: Actualizar lista de usuarios
  if (event.type === 'USERLIST' && event.channel) {
    addLog(`📋 USERLIST recibido para ${event.channel}`);
    setTimeout(() => {
      fetchChannelUsersRef.current(event.channel!);
    }, 300);
  }
});
  unsubscribers.push(unsubEvent);

  const unsubError = linkHubIrcService.onError((error: IRCMessage) => {
    addLog(`⚠️ Error IRC: ${error.text}`);
    console.error('IRC Error:', error);
  });
  unsubscribers.push(unsubError);

  const unsubStats = linkHubIrcService.onStats((stats: IRCStats) => {
    addLog(`📊 Stats actualizados: ${stats.total_users} usuarios`);
    setIrcStats(stats);
  });
  unsubscribers.push(unsubStats);

  addLog('✅ Listeners configurados correctamente');

  // ✅ CLEANUP mejorado
  return () => {
    addLog('🔌 Desconectando listeners...');
    listenersAttachedRef.current = false;
    
    // Ejecutar TODOS los unsubscribers
    unsubscribers.forEach(unsub => {
      try {
        unsub();
      } catch (e) {
        console.error('Error al desuscribir:', e);
      }
    });
  };
}, []); // ✅ Array vacío - solo se ejecuta UNA VEZ al montar

  // ═══════════════════════════════════════════════════════════════════════════
  // AUTO-SCROLL SILENCIOSO PARA mIRC32
  // ═══════════════════════════════════════════════════════════════════════════
  // Obtener mensajes del canal activo para el efecto
  const activeChannelMessagesForScroll = ircMessages.get(mircActiveChannel) || [];
  
  useEffect(() => {
    const chatContainer = mircChatRef.current;
    if (!chatContainer) return;
    
    // ═══════════════════════════════════════════════════════════════════
    // DETECTAR SI EL USUARIO SCROLLEÓ HACIA ARRIBA
    // ═══════════════════════════════════════════════════════════════════
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // Si está a más de 100px del fondo, el usuario está leyendo historial
      const wasScrolledUp = mircUserScrolledUp.current;
      mircUserScrolledUp.current = distanceFromBottom > 100;
      
      // Si el usuario bajó hasta el fondo, ocultar indicador de nuevos mensajes
      if (wasScrolledUp && !mircUserScrolledUp.current) {
        setMircHasNewMessages(false);
      }
    };
    
    chatContainer.addEventListener('scroll', handleScroll);
    
    return () => {
      chatContainer.removeEventListener('scroll', handleScroll);
    };
  }, [mircActiveChannel]); // Re-attach cuando cambia el canal
  
  useEffect(() => {
    // ═══════════════════════════════════════════════════════════════════
    // AUTO-SCROLL SOLO SI:
    // 1. Hay mensajes nuevos
    // 2. El usuario NO está leyendo historial (no scrolleó hacia arriba)
    // ═══════════════════════════════════════════════════════════════════
    const currentCount = activeChannelMessagesForScroll.length;
    const prevCount = mircPrevMessageCount.current;
    
    // Si hay mensajes nuevos
    if (currentCount > prevCount) {
      if (!mircUserScrolledUp.current) {
        // Usuario está abajo → auto-scroll silencioso SOLO del contenedor IRC
        requestAnimationFrame(() => {
          const chatContainer = mircChatRef.current;
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        });
        setMircHasNewMessages(false);
      } else {
        // Usuario está leyendo historial → mostrar indicador
        setMircHasNewMessages(true);
      }
    }
    
    // Actualizar contador
    mircPrevMessageCount.current = currentCount;
    
  }, [activeChannelMessagesForScroll.length, mircActiveChannel]);
  
  // Resetear scroll cuando cambia de canal
  useEffect(() => {
    mircUserScrolledUp.current = false;
    mircPrevMessageCount.current = 0;
    setMircHasNewMessages(false);
    
    // Scroll al fondo del nuevo canal SOLO del contenedor IRC
    requestAnimationFrame(() => {
      const chatContainer = mircChatRef.current;
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    });
  }, [mircActiveChannel]);

  // ═══════════════════════════════════════════════════════════════════════════
  // ROTAR HISTORIAL DE MENSAJES CADA HORA
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const rotateHistory = () => {
      setMessageHistory(prev => {
        const newMap = new Map(prev);
        
        for (const [channel, history] of newMap.entries()) {
          // Shift left: quitar el más antiguo, agregar 0 al final
          const rotated = [...history.slice(1), 0];
          newMap.set(channel, rotated);
        }
        
        console.log('📊 Historial de mensajes rotado (nueva hora)');
        return newMap;
      });
    };
    
    // Calcular cuánto falta para la próxima hora
    const now = new Date();
    const msUntilNextHour = (60 - now.getMinutes()) * 60 * 1000 - now.getSeconds() * 1000;
    
    // Primera rotación al inicio de la próxima hora
    const initialTimeout = setTimeout(() => {
      rotateHistory();
      
      // Luego cada hora
      const interval = setInterval(rotateHistory, 60 * 60 * 1000);
      
      return () => clearInterval(interval);
    }, msUntilNextHour);
    
    return () => clearTimeout(initialTimeout);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // VERIFICAR ESTADO DE CONEXIÓN AL MONTAR
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
  const checkConnection = () => {
    const connected = linkHubIrcService.isConnected();
    
    // ✅ Solo actualizar si el estado cambió
    setIrcConnected(prev => {
      if (prev !== connected) {
        if (connected) {
          addLog('✅ Servicio IRC conectado - Iniciando precarga silenciosa...');
          
          // ✅ PRECARGA SILENCIOSA: Cargar todo al conectar
          setTimeout(async () => {
            // 1. Refrescar canales
            await refreshChannels();
            
            // 2. Refrescar stats
            await refreshStats();
            
            // 3. Cargar usuarios de TODOS los canales
            const channels = await linkHubIrcService.getChannels();
            addLog(`📺 Precarga: ${channels.length} canales detectados`);
            
            for (const channel of channels) {
              addLog(`👥 Precargando usuarios de ${channel.name}...`);
              await fetchChannelUsers(channel.name);
              await new Promise(r => setTimeout(r, 300)); // Pequeño delay entre canales
            }
            
            addLog('✅ Precarga silenciosa completada');
          }, 500);
        } else {
          addLog('ℹ️ Servicio IRC desconectado');
        }
        return connected;
      }
      return prev;
    });
  };

  checkConnection();
  
  // ✅ Aumentar intervalo a 10 segundos para reducir overhead
  const interval = setInterval(checkConnection, 10000);
  return () => clearInterval(interval);
}, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // CARGAR USUARIOS CUANDO CAMBIA EL CANAL ACTIVO
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
  if (!ircConnected || !mircActiveChannel) return;
  
  addLog(`👥 Cargando usuarios de ${mircActiveChannel}...`);
  
  // ✅ Flag para prevenir race conditions
  let isActive = true;
  let currentFetchId = Date.now();
  
  const loadUsers = async () => {
    if (!isActive) {
      addLog(`⚠️ Cancelando fetch anterior de usuarios (canal cambió)`);
      return;
    }
    
    const fetchId = currentFetchId;
    addLog(`🔄 [${fetchId}] Iniciando fetch de usuarios...`);
    
    await fetchChannelUsers(mircActiveChannel);
    
    if (!isActive) {
      addLog(`⚠️ [${fetchId}] Fetch completado pero ya no es relevante`);
    } else {
      addLog(`✅ [${fetchId}] Fetch completado y aplicado`);
    }
  };
  
  // ✅ Cargar inmediatamente
  loadUsers();
  
  // ✅ Auto-refresh más espaciado (60 segundos en vez de 30)
  const interval = setInterval(() => {
    if (isActive) {
      loadUsers();
    }
  }, 60000);
  
  return () => {
    isActive = false;
    clearInterval(interval);
    addLog(`🧹 Cleanup: deteniendo auto-refresh de ${mircActiveChannel}`);
  };
}, [mircActiveChannel, ircConnected]); 

  // ═══════════════════════════════════════════════════════════════════════════
  // CONECTAR A IRC
  // ═══════════════════════════════════════════════════════════════════════════
  const handleIrcConnect = async () => {
    try {
      addLog('========================================');
      addLog('🔌 INICIANDO CONEXIÓN A IRC');
      addLog('========================================');
      addLog(`📋 Servidor: ${ircConfig.server}:${ircConfig.port}`);
      addLog(`👤 Nickname: ${ircConfig.nickname}`);
      addLog(`🔒 SSL: ${ircConfig.useSSL ? 'Habilitado' : 'Deshabilitado'}`);
      addLog(`📺 Canales: ${ircConfig.channels?.join(', ')}`);
      addLog(`🔄 Alternativas: ${ircConfig.alternativeNicks?.join(', ')}`);
      addLog('----------------------------------------');
      
      setSkillMessage('⏳ Conectando...');
      setShowConfetti(false);
      
      addLog('📡 Llamando a linkHubIrcService.connect()...');
      const connectionId = await linkHubIrcService.connect(ircConfig);
      
      addLog('✅ CONEXIÓN EXITOSA!');
      addLog(`🆔 Connection ID: ${connectionId}`);
      
      setIrcConnected(true);
      setShowIrcConfig(false);
      setSkillMessage('✅ Conectado!');
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
      
      // Refrescar datos
      await refreshChannels();
      await refreshStats();
      
      // ✅ Cargar usuarios de todos los canales después de un delay mayor
      setTimeout(async () => {
        addLog('🔄 Cargando usuarios de todos los canales...');
        const channels = await linkHubIrcService.getChannels();
        addLog(`📺 Canales detectados: ${channels.map(c => c.name).join(', ')}`);
        
        for (const channel of channels) {
          addLog(`👥 Solicitando usuarios de ${channel.name}...`);
          
          // ✅ Retry logic: intentar hasta 3 veces con delays incrementales
          let attempt = 1;
          const maxAttempts = 3;
          
          const tryFetch = async () => {
            await fetchChannelUsers(channel.name);
            
            // Verificar si obtuvimos usuarios
            const users = ircUsers.get(channel.name) || [];
            
            if (users.length === 0 && attempt < maxAttempts) {
              attempt++;
              const delay = attempt * 1000; // 1s, 2s, 3s
              addLog(`⚠️ No se obtuvieron usuarios de ${channel.name}, reintentando en ${delay}ms... (intento ${attempt}/${maxAttempts})`);
              
              await new Promise(r => setTimeout(r, delay));
              await tryFetch();
            } else if (users.length > 0) {
              addLog(`✅ ${users.length} usuarios cargados de ${channel.name}`);
            } else {
              addLog(`❌ No se pudieron cargar usuarios de ${channel.name} después de ${maxAttempts} intentos`);
            }
          };
          
          await tryFetch();
          await new Promise(r => setTimeout(r, 500)); // Esperar entre canales
        }
        
        addLog('✅ Carga inicial de usuarios completada');
      }, 4000); // ✅ Esperar 4 segundos para que IRC se estabilice completamente
      
      addLog('========================================');
      
    } catch (error: any) {
      addLog('========================================');
      addLog('❌ ERROR EN CONEXIÓN IRC');
      addLog('========================================');
      addLog(`Error: ${error.message || error.toString()}`);
      addLog(`Stack: ${error.stack?.substring(0, 200)}`);
      addLog('========================================');
      
      console.error('Error completo:', error);
      
      const errorMsg = error.message || error.toString() || 'Error desconocido';
      showNotification('error', 'Error al conectar a LinkHub', `${errorMsg}\n\nRevisa la consola del navegador (F12) para más detalles.`);
      
      setSkillMessage('');
      setShowConfetti(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // DESCONECTAR DE IRC
  // ═══════════════════════════════════════════════════════════════════════════
  const handleIrcDisconnect = async () => {
    try {
      addLog('🔌 Desconectando de IRC...');
      await linkHubIrcService.disconnect();
      addLog('✅ Desconectado correctamente');
      setIrcConnected(false);
      setIrcMessages(new Map());
      setIrcChannels([]);
      setIrcStats(null);
    } catch (error) {
      addLog(`❌ Error al desconectar: ${error}`);
      console.error('Error al desconectar:', error);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // TEST BACKEND
  // ═══════════════════════════════════════════════════════════════════════════
  const handleTestBackend = async () => {
    try {
      addLog('🧪 Testeando conectividad con el backend...');
      addLog('📡 Verificando API endpoint...');
      
      const testUrl = 'https://backend.anubisai.net/api/linkhub/test';
      addLog(`🌐 URL de prueba: ${testUrl}`);
      
      const token = localStorage.getItem('hatateToken');
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      addLog(`📊 Response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        addLog('✅ Backend accesible correctamente');
        addLog(`📄 Response: ${JSON.stringify(data)}`);
        showNotification('success', 'Backend está accesible!', 'Puedes proceder a conectar a LinkHub.');
      } else {
        addLog(`⚠️ Backend respondió con error: ${response.status} ${response.statusText}`);
        const text = await response.text();
        addLog(`📄 Response body: ${text.substring(0, 100)}`);
        showNotification('error', 'Backend respondió con error', `${response.status} ${response.statusText}`);
      }
    } catch (error: any) {
      addLog(`❌ Error al testear backend: ${error.message}`);
      console.error('Error test backend:', error);
      showNotification('error', 'No se puede conectar al backend', `${error.message}\n\nVerifica que el servidor esté ejecutándose.`);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // UNIRSE A CANAL
  // ═══════════════════════════════════════════════════════════════════════════
  const handleJoinChannel = async (channel: string) => {
    if (!ircConnected) {
      showNotification('warning', 'Debes estar conectado al servidor primero');
      return;
    }
    
    // ✅ Normalizar el nombre del canal: agregar # si no lo tiene
    const normalizedChannel = channel.startsWith('#') ? channel : `#${channel}`;
    
    try {
      addLog(`➕ Uniéndose a ${normalizedChannel}...`);
      await linkHubIrcService.joinChannel(normalizedChannel);
      addLog(`✅ Join solicitado para ${normalizedChannel}`);
      showNotification('success', `Te uniste a ${normalizedChannel}`);
      
      // Refrescar canales y cargar usuarios después de un momento
      setTimeout(async () => {
        await refreshChannels();
        await fetchChannelUsers(normalizedChannel);
      }, 1500);
    } catch (error: any) {
      addLog(`❌ Error al unirse a ${normalizedChannel}: ${error.message}`);
      showNotification('error', `Error al unirse a ${normalizedChannel}`, error.message);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SALIR DE CANAL
  // ═══════════════════════════════════════════════════════════════════════════
  const handlePartChannel = async (channel: string) => {
    if (!ircConnected) return;
    
    try {
      addLog(`➖ Saliendo de ${channel}...`);
      await linkHubIrcService.partChannel(channel);
      addLog(`✅ Part solicitado para ${channel}`);
      
      // Refrescar canales después de un momento
      setTimeout(refreshChannels, 1000);
    } catch (error: any) {
      addLog(`❌ Error al salir de ${channel}: ${error.message}`);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // GENERAR COMMUNITIES DESDE DATOS REALES
  // ═══════════════════════════════════════════════════════════════════════════
  const getCommunities = (): Community[] => {
  if (ircChannels.length === 0) {
    // Fallback a mock si no hay datos reales
    return [
      {
        id: '1',
        name: '#general',
        topic: 'Canal general de la comunidad',
        users: 0,
        messages: 0,
        lastMessage: { user: 'system', text: 'Conecta a la red LinkHub para ver actividad', time: 'now' },
        activity: [12, 18, 15, 22, 19, 25, 21],
        status: 'quiet',
        description: 'Espacio principal para conectar con la comunidad',
        category: 'General',
        avatar: 'https://ui-avatars.com/api/?name=General&background=007AFF&color=fff&size=120',
        verified: true
      }
    ];
  }

  const now = Date.now();
  
  return ircChannels.map((ch, idx) => {
    const messages = ircMessages.get(ch.name) || [];
    const lastMsg = messages[messages.length - 1];
    
    // ✅ Contar usuarios reales desde ircUsers Map
    const channelUsers = ircUsers.get(ch.name) || [];
    const userCount = channelUsers.length;
    
    // ✅ Calcular estado basado en última actividad
    const lastActivityTime = lastActivity.get(ch.name) || 0;
    const timeSinceActivity = now - lastActivityTime;
    
    let status: 'hot' | 'active' | 'quiet';
    if (timeSinceActivity < 60000) {
      status = 'hot';
    } else if (timeSinceActivity < 300000) {
      status = 'active';
    } else {
      status = 'quiet';
    }
    
    // ✅ Usar historial real de mensajes
    const activityData = messageHistory.get(ch.name) || Array(7).fill(0);
    
    // ✅ NUEVO: Generar metadata social
    const channelName = ch.name.replace('#', '');
    const isVerified = ['linkhub', 'general', 'tech', 'dev'].includes(channelName.toLowerCase());
    const isTrending = status === 'hot' && userCount > 10;
    
    // Generar descripción basada en topic o default
    const description = ch.topic || 
      `Comunidad de ${channelName}. Conecta, comparte y colabora con ${userCount} miembros activos.`;
    
    // Avatar generado dinámicamente por inicial
    const avatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(channelName)}&background=${
      status === 'hot' ? 'FF3B30' : status === 'active' ? '34C759' : '8E8E93'
    }&color=fff&size=120&bold=true`;
    
    // Categoría basada en nombre del canal
    let category = 'General';
    if (channelName.match(/tech|dev|code|program/i)) category = 'Tecnología';
    else if (channelName.match(/game|gaming|play/i)) category = 'Gaming';
    else if (channelName.match(/music|art|design/i)) category = 'Arte y Cultura';
    else if (channelName.match(/help|support|ayuda/i)) category = 'Soporte';
    
    return {
      id: `ch_${idx}`,
      name: ch.name,
      topic: ch.topic || 'Comunidad LinkHub activa',
      users: userCount,
      messages: messages.length,
      lastMessage: lastMsg 
        ? { 
            user: lastMsg.from || 'usuario', 
            text: lastMsg.text || '', 
            time: new Date(lastMsg.timestamp).toLocaleTimeString()
          }
        : { user: 'system', text: 'Sé el primero en enviar un mensaje', time: 'now' },
      activity: activityData,
      status,
      // ✅ Campos sociales
      avatar,
      description,
      category,
      verified: isVerified,
      trending: isTrending,
      tags: [channelName, category.toLowerCase()],
      // TODO: Conectar con APIs externas más adelante
      externalLinks: {}
    };
  });
};

  // ═══════════════════════════════════════════════════════════════════════════
  // MOCK DATA (fallback cuando no hay datos reales)
  // ═══════════════════════════════════════════════════════════════════════════
  const mockUsers: User[] = Array.from({ length: 5 }, (_, i) => ({
    id: String(i + 1),
    nick: `user${i + 1}`,
    host: `user${i + 1}@example.com`,
    privilege: i === 0 ? 'owner' : i < 3 ? 'op' : i < 8 ? 'voice' : 'user',
    channels: ['#general'],
    lastSeen: `${Math.floor(Math.random() * 60)}m`,
    messages: Math.floor(Math.random() * 1000)
  }));

  const mockChannels: Channel[] = [
    { id: '1', name: '#general', topic: 'Canal general', users: 234, messages: 1547, mode: '+nt', created: '2024-01-15' }
  ];

  const communities = getCommunities();
  const channels = ircChannels.length > 0 
    ? ircChannels.map((ch, idx) => {
        // ✅ Contar usuarios reales desde ircUsers Map
        const channelUsers = ircUsers.get(ch.name) || [];
        return {
          id: `ch_${idx}`,
          name: ch.name,
          topic: ch.topic || 'Comunidad LinkHub activa',
          users: channelUsers.length,  // ✅ Conteo real
          messages: (ircMessages.get(ch.name) || []).length,
          mode: '+nt',
          created: new Date().toISOString().split('T')[0]
        };
      })
    : mockChannels;

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const openChatWindow = (channelName: string, type: 'channel' | 'private' = 'channel') => {
    const windowId = `chat-${channelName}-${Date.now()}`;
    
    // ✅ Tamaño diferente para canales (con panel) vs privados (sin panel)
    const isChannel = channelName.startsWith('#');
    const windowSize = isChannel 
      ? { width: 700, height: 625 }  // Canal: más ancho para panel de audiencia
      : { width: 420, height: 500 }; // Privado: más compacto
    
    // Posición escalonada para evitar superposición
    const existingWindows = windowManager.windows.length;
    const offset = existingWindows * 25;
    
    windowManager.registerWindow({
      id: windowId,
      type: 'widget',
      conversationId: channelName,
      position: { x: 200 + offset, y: 80 + offset },
      size: windowSize,
      metadata: { channelName, chatType: type, isChannel }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'hot': return '#ff3b30';
      case 'active': return '#00ff9d';
      case 'quiet': return '#888888';
      default: return '#888888';
    }
  };

  const getPrivilegeIcon = (priv: string) => {
    switch (priv) {
      case 'owner': return { icon: faCrown, color: '#ff0000' };
      case 'op': return { icon: faUserShield, color: '#00ff00' };
      default: return { icon: faCircle, color: '#888888' };
    }
  };

  const handleCommand = (cmd: string) => {
    const command = cmd.trim().toLowerCase();

    // Easter eggs locales
    if (command === '/abracadabra!') {
      if (!mirc32Unlocked) {
        localStorage.setItem('skill_mirc32', 'true');
        setMirc32Unlocked(true);
        setSkillMessage('🎉 ¡SKILL DESBLOQUEADO! 🎉\n\nmIRC32 Mode activado');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 15000);
      }
      return;
    } else if (command === '/matilde!') {
      if (mirc32Unlocked && !themesUnlocked) {
        localStorage.setItem('skill_themes', 'true');
        setThemesUnlocked(true);
        setSkillMessage('🎊 ¡SKILL DESBLOQUEADO! 🎊\n\nThemes Selector activado');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 15000);
      }
      return;
    }

    // Si no es easter egg y estamos conectados, procesar comando IRC
    if (ircConnected && mircActiveChannel) {
      // Detectar si es un comando IRC (empieza con /)
      if (cmd.startsWith('/')) {
        // Parsear comando: /whois Paula → command="WHOIS", args=["Paula"]
        const parts = cmd.slice(1).split(' ').filter(p => p.trim());
        if (parts.length === 0) return;
        
        const ircCommand = parts[0].toUpperCase();
        const args = parts.slice(1);
        
        console.log(`🎮 Ejecutando comando IRC: ${ircCommand} ${args.join(' ')}`);
        
        linkHubIrcService.executeCommand(ircCommand, args).catch(error => {
          console.error(`❌ Error ejecutando ${ircCommand}:`, error);
        });
      } else {
        // No es comando, enviar como mensaje normal
        linkHubIrcService.sendMessage(mircActiveChannel, cmd);
      }
    }
  };

  const Confetti: React.FC = () => {
    const confettiPieces = Array.from({ length: 80 });
    return (
      <div className="confetti-container">
        {confettiPieces.map((_, i) => (
          <div
            key={i}
            className="confetti-piece"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              background: ['#ff0066', '#00ff9d', '#00d4ff', '#ffaa00', '#ff3b30'][Math.floor(Math.random() * 5)]
            }}
          />
        ))}
      </div>
    );
  };

  const MiniSparkline: React.FC<{ data: number[] }> = ({ data }) => {
    const width = 80;
    const height = 30;
    const max = Math.max(...data);
    const step = width / (data.length - 1);

    const points = data.map((value, i) => ({
      x: i * step,
      y: height - (value / max) * height
    }));

    const pathData = points.map((p, i) => 
      `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`
    ).join(' ');

    return (
      <svg width={width} height={height} style={{ display: 'block' }}>
        <path
          d={pathData}
          fill="none"
          stroke="#00d4ff"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  const ChatWindow: React.FC<{ 
    window: any; 
    channelName: string;
    onClose: () => void;
    onMinimize: () => void;
    onBringToFront: () => void;
  }> = ({ window: win, channelName, onClose, onMinimize, onBringToFront }) => {
    // ✅ USAR REF para el valor del input (evita pérdida en re-renders)
    const inputValueRef = useRef('');
    const [, forceUpdate] = useState(0); // Para forzar re-render si es necesario
    
    const windowRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    
    // ✅ DETECTAR SI ES CANAL O PRIVADO
    const isChannel = channelName.startsWith('#');
    
    // ✅ Estados para panel de audiencia (SOLO CANALES)
    const [showAudiencePanel, setShowAudiencePanel] = useState(isChannel);
    const [audiencePanelWidth, setAudiencePanelWidth] = useState(280);
    const [graphHeightPercent, setGraphHeightPercent] = useState(60);
    
    // ✅ Estado para menú contextual de usuarios
    const [userContextMenu, setUserContextMenu] = useState<{
      x: number;
      y: number;
      user: IRCUser;
    } | null>(null);

    const channelMessages = ircMessages.get(channelName) || [];
    const channelUsersList = isChannel ? (ircUsers.get(channelName) || []) : [];
    
    // ✅ DETECTAR MI PRIVILEGIO EN ESTE CANAL
    const myNickname = ircConfig.nickname;
    
    // ✅ Buscar mi usuario en la lista (comparación case-insensitive)
    const myUserData = channelUsersList.find(u => 
      u.nick.toLowerCase() === myNickname.toLowerCase()
    );
    const myPrivilege = myUserData?.privilege || 'user';
    const amIOperator = myPrivilege === 'owner' || myPrivilege === 'op';
    
    // DEBUG: Log para verificar detección de privilegio
    useEffect(() => {
      if (channelUsersList.length > 0) {
        console.log(`🔍 [DEBUG] Canal: ${channelName}`);
        console.log(`🔍 [DEBUG] Mi nickname: "${myNickname}"`);
        console.log(`🔍 [DEBUG] Usuarios en canal:`, channelUsersList.map(u => `${u.nick} (${u.privilege})`));
        console.log(`🔍 [DEBUG] Mi usuario encontrado:`, myUserData);
        console.log(`🔍 [DEBUG] Mi privilegio: ${myPrivilege}`);
        console.log(`🔍 [DEBUG] ¿Soy operador?: ${amIOperator}`);
      }
    }, [channelUsersList, myNickname, myUserData, myPrivilege, amIOperator, channelName]);
    
    // ✅ FIX SCROLL: Solo auto-scroll con mensajes NUEVOS
    const prevMessagesCountRef = useRef(0);
    
    useEffect(() => {
      // Solo hacer scroll si hay MÁS mensajes que antes
      if (channelMessages.length > prevMessagesCountRef.current) {
        // Scroll manual del contenedor (no afecta al documento global)
        const container = messagesContainerRef.current;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }
      prevMessagesCountRef.current = channelMessages.length;
    }, [channelMessages.length]);
    
    // ✅ Cerrar menú contextual al hacer click fuera
    useEffect(() => {
      const handleClickOutside = () => setUserContextMenu(null);
      if (userContextMenu) {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
      }
    }, [userContextMenu]);

    // ═══════════════════════════════════════════════════════════════════════════
    // DRAG HANDLERS
    // ═══════════════════════════════════════════════════════════════════════════
    
    const handleMouseDown = (e: React.MouseEvent) => {
      e.preventDefault();
      onBringToFront();

      const startX = e.clientX;
      const startY = e.clientY;
      const startPosX = win.position.x;
      const startPosY = win.position.y;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;
        
        windowManager.updateWindowPosition(win.id, {
          x: startPosX + deltaX,
          y: startPosY + deltaY
        });
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    // ✅ NUEVO: Drag handler para divisor horizontal (chat/panel)
    const handlePanelDividerDrag = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const startX = e.clientX;
      const startWidth = audiencePanelWidth;
      const windowWidth = win.size.width;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaX = startX - moveEvent.clientX;
        const newWidth = Math.max(180, Math.min(windowWidth * 0.6, startWidth + deltaX));
        setAudiencePanelWidth(newWidth);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    // ✅ NUEVO: Drag handler para divisor vertical (grafo/userlist)
    const handleGraphDividerDrag = (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const container = e.currentTarget.parentElement;
      if (!container) return;
      
      const containerRect = container.getBoundingClientRect();
      const startY = e.clientY;
      const startPercent = graphHeightPercent;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const deltaY = moveEvent.clientY - startY;
        const deltaPercent = (deltaY / containerRect.height) * 100;
        const newPercent = Math.max(30, Math.min(80, startPercent + deltaPercent));
        setGraphHeightPercent(newPercent);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    // ✅ FIX: Usar ref para manejar el input (evita pérdida de texto)
    const handleLocalCommand = async () => {
      const inputValue = inputRef.current?.value || '';
      if (!inputValue.trim()) return;
      
      if (inputValue.startsWith('/')) {
        handleCommand(inputValue);
      } else if (ircConnected) {
        await linkHubIrcService.sendMessage(channelName, inputValue);
      }
      
      // Limpiar input
      if (inputRef.current) {
        inputRef.current.value = '';
        inputValueRef.current = '';
      }
      
      // Mantener focus
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    };
    
    // Handler para cambio de input (guarda en ref)
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      inputValueRef.current = e.target.value;
    };
    
    // ✅ NUEVO: Handler para doble clic en usuario (abrir privado)
    const handleUserDoubleClick = (user: IRCUser) => {
      // ✅ Bloquear si es mi propio nick
      if (user.nick.toLowerCase() === myNickname.toLowerCase()) {
        console.log(`🚫 No puedes abrir privado contigo mismo`);
        return;
      }
      console.log(`📩 Abriendo privado con ${user.nick}`);
      openChatWindow(user.nick, 'private');
    };
    
    // ✅ NUEVO: Handler para click derecho en usuario (menú contextual)
    const handleUserContextMenu = (e: React.MouseEvent, user: IRCUser) => {
      e.preventDefault();
      e.stopPropagation();
      
      // ✅ MEJORADO: Calcular posición relativa a la VENTANA PADRE
      const menuWidth = 260;   // Ancho máximo del menú
      const menuHeight = 450;  // Altura estimada del menú con todas las opciones
      const padding = 10;      // Margen del borde
      
      // ═══════════════════════════════════════════════════════════════════
      // OBTENER COORDENADAS RELATIVAS AL CONTENEDOR
      // ═══════════════════════════════════════════════════════════════════
      if (!windowRef.current) {
        console.warn('⚠️ windowRef no disponible');
        return;
      }
      
      const containerRect = windowRef.current.getBoundingClientRect();
      
      // Convertir coordenadas globales → relativas al contenedor
      const clickXGlobal = e.clientX;
      const clickYGlobal = e.clientY;
      const clickXRelative = clickXGlobal - containerRect.left;
      const clickYRelative = clickYGlobal - containerRect.top;
      
      // Dimensiones del contenedor
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      let x = clickXRelative;
      let y = clickYRelative;
      
      // ═══════════════════════════════════════════════════════════════════
      // HORIZONTAL: Si no cabe a la derecha, abrir a la izquierda del click
      // ═══════════════════════════════════════════════════════════════════
      const wouldOverflowRight = (x + menuWidth + padding) > containerWidth;
      
      if (wouldOverflowRight) {
        // Intentar abrir a la izquierda del click
        x = x - menuWidth;
      }
      
      // Forzar dentro de los límites del contenedor
      if (x + menuWidth + padding > containerWidth) {
        x = containerWidth - menuWidth - padding;
      }
      if (x < padding) {
        x = padding;
      }
      
      // ═══════════════════════════════════════════════════════════════════
      // VERTICAL: Si no cabe abajo, abrir hacia arriba
      // ═══════════════════════════════════════════════════════════════════
      const wouldOverflowBottom = (y + menuHeight + padding) > containerHeight;
      
      if (wouldOverflowBottom) {
        // Intentar abrir hacia arriba
        y = y - menuHeight;
      }
      
      // Forzar dentro de los límites del contenedor
      if (y + menuHeight + padding > containerHeight) {
        y = containerHeight - menuHeight - padding;
      }
      if (y < padding) {
        y = padding;
      }
      
      console.log(`📍 Menú: clickGlobal(${clickXGlobal},${clickYGlobal}) → clickRelative(${clickXRelative},${clickYRelative}) → final(${x},${y})`);
      
      setUserContextMenu({ x, y, user });
    };
    
    // ✅ NUEVO: Ejecutar acción del menú contextual
    const handleUserAction = async (action: string, user: IRCUser) => {
      console.log(`🎯 Acción: ${action} sobre ${user.nick}`);
      setUserContextMenu(null);
      
      if (!ircConnected) {
        alert('⚠️ Debes estar conectado al servidor para realizar esta acción');
        return;
      }

      // Helper para enviar comandos IRC raw
      const sendIrcCommand = async (cmd: string) => {
        console.log(`📤 Procesando comando: ${cmd}`);
        
        // Detectar si es un comando IRC (empieza con /)
        if (cmd.startsWith('/')) {
          // Parsear comando: /whois Paula → command="WHOIS", args=["Paula"]
          const parts = cmd.slice(1).split(' ').filter(p => p.trim());
          if (parts.length === 0) return;
          
          const command = parts[0].toUpperCase();
          const args = parts.slice(1);
          
          console.log(`🎮 Comando IRC detectado: ${command} ${args.join(' ')}`);
          
          try {
            await linkHubIrcService.executeCommand(command, args);
            
            // Feedback visual en la ventana
            addCommandFeedback(
              channelName, 
              `→ ${command} ${args.join(' ')}`, 
              'sent'
            );
          } catch (error: any) {
            console.error(`❌ Error ejecutando ${command}:`, error);
            addCommandFeedback(
              channelName, 
              `❌ Error: ${error.message}`, 
              'error'
            );
          }
        } else {
          // No es comando, enviar como mensaje normal
          await linkHubIrcService.sendMessage(channelName, cmd);
        }
      };

      switch (action) {
        // ═══════════════════════════════════════════════════════════════════
        // ACCIONES BÁSICAS (cualquier usuario)
        // ═══════════════════════════════════════════════════════════════════
        case 'query':
          // ✅ Bloquear si es mi propio nick
          if (user.nick.toLowerCase() === myNickname.toLowerCase()) {
            console.log(`🚫 No puedes abrir privado contigo mismo`);
            return;
          }
          openChatWindow(user.nick, 'private');
          break;
        case 'whois':
          await sendIrcCommand(`/whois ${user.nick}`);
          break;
        
        // ═══════════════════════════════════════════════════════════════════
        // ACCIONES DE OPERADOR DE CANAL (@)
        // ═══════════════════════════════════════════════════════════════════
        case 'op':
          await sendIrcCommand(`/mode ${channelName} +o ${user.nick}`);
          break;
        case 'deop':
          await sendIrcCommand(`/mode ${channelName} -o ${user.nick}`);
          break;
        case 'voice':
          await sendIrcCommand(`/mode ${channelName} +v ${user.nick}`);
          break;
        case 'devoice':
          await sendIrcCommand(`/mode ${channelName} -v ${user.nick}`);
          break;
        case 'kick':
          showActionPrompt(
            `Expulsar a ${user.nick}`,
            'Razón del kick',
            'Comportamiento inadecuado',
            'Ingresa la razón...',
            async (kickReason) => {
              await sendIrcCommand(`/kick ${channelName} ${user.nick} ${kickReason}`);
            }
          );
          break;
        case 'ban':
          const banMask = user.host ? `*!*@${user.host.split('@').pop()}` : user.nick;
          await sendIrcCommand(`/mode ${channelName} +b ${banMask}`);
          break;
        case 'kickban':
          showActionPrompt(
            `Kickban a ${user.nick}`,
            'Razón del kickban',
            'Violación de reglas',
            'Ingresa la razón...',
            async (kbReason) => {
              const banMask = user.host ? `*!*@${user.host.split('@').pop()}` : user.nick;
              await sendIrcCommand(`/mode ${channelName} +b ${banMask}`);
              await sendIrcCommand(`/kick ${channelName} ${user.nick} ${kbReason}`);
            }
          );
          break;
        case 'unban':
          const unbanMask = prompt(`Máscara a desbanear:`, user.host ? `*!*@${user.host.split('@').pop()}` : user.nick);
          if (unbanMask) {
            await sendIrcCommand(`/mode ${channelName} -b ${unbanMask}`);
          }
          break;
        
        // ═══════════════════════════════════════════════════════════════════
        // ACCIONES IRCOP (operador de red)
        // ═══════════════════════════════════════════════════════════════════
        case 'kill':
          const killReason = prompt(`Razón del KILL a ${user.nick}:`, 'Desconectado por IRCop');
          if (killReason !== null) {
            await sendIrcCommand(`/kill ${user.nick} ${killReason}`);
          }
          break;
        case 'kline':
          const klineTime = prompt(`Duración del K-Line (ej: 1h, 1d, 0 para permanente):`, '1h');
          const klineReason = prompt(`Razón del K-Line:`, 'Violación de políticas de red');
          if (klineTime !== null && klineReason !== null) {
            const hostMask = user.host ? user.host.split('@').pop() : user.nick;
            await sendIrcCommand(`/kline ${klineTime} *@${hostMask} ${klineReason}`);
          }
          break;
        case 'gline':
          const glineTime = prompt(`Duración del G-Line global (ej: 1h, 1d, 0 para permanente):`, '1d');
          const glineReason = prompt(`Razón del G-Line:`, 'Ban global por abuso');
          if (glineTime !== null && glineReason !== null) {
            const hostMask = user.host ? user.host.split('@').pop() : user.nick;
            await sendIrcCommand(`/gline *@${hostMask} ${glineTime} ${glineReason}`);
          }
          break;
        case 'zline':
          const zlineTime = prompt(`Duración del Z-Line (ban IP, ej: 1h, 1d):`, '1h');
          const zlineReason = prompt(`Razón del Z-Line:`, 'IP bloqueada');
          if (zlineTime !== null && zlineReason !== null) {
            const ip = user.host ? user.host.split('@').pop() : '';
            if (ip) {
              await sendIrcCommand(`/zline ${ip} ${zlineTime} ${zlineReason}`);
            } else {
              alert('No se pudo determinar la IP del usuario');
            }
          }
          break;
        case 'shun':
          const shunTime = prompt(`Duración del SHUN (silenciar, ej: 1h, 1d):`, '1h');
          const shunReason = prompt(`Razón del SHUN:`, 'Spam/Flood');
          if (shunTime !== null && shunReason !== null) {
            await sendIrcCommand(`/shun ${user.nick} ${shunTime} ${shunReason}`);
          }
          break;
        case 'sajoin':
          const sajoinChannel = prompt(`Forzar entrada a canal:`, '#ayuda');
          if (sajoinChannel) {
            await sendIrcCommand(`/sajoin ${user.nick} ${sajoinChannel}`);
          }
          break;
        case 'sapart':
          const sapartChannel = prompt(`Forzar salida de canal:`, channelName);
          if (sapartChannel) {
            await sendIrcCommand(`/sapart ${user.nick} ${sapartChannel}`);
          }
          break;
        
        default:
          console.warn('Acción desconocida:', action);
      }
    };

    // ✅ NUEVO: Obtener color e icono de privilegio para userlist
    const getUserPrivilegeInfo = (privilege: string) => {
      switch (privilege) {
        case 'owner': return { prefix: '~', color: '#e74c3c', icon: '👑' };
        case 'op': return { prefix: '@', color: '#2ecc71', icon: '🛡️' };
        case 'voice': return { prefix: '+', color: '#3498db', icon: '🎤' };
        default: return { prefix: '', color: '#95a5a6', icon: '' };
      }
    };

    return (
      <div
        ref={windowRef}
        className="chat-window-aqua"
        style={{
          position: 'fixed',
          left: win.position.x,
          top: win.position.y,
          width: win.size.width,
          height: win.size.height,
          zIndex: 100000 + win.zIndex  // ✅ Base alto para estar sobre todo el frontend
        }}
        onMouseDown={onBringToFront}
      >
        {/* ═══════════════════════════════════════════════════════════════════
            TITLEBAR
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="chat-titlebar-aqua" onMouseDown={handleMouseDown}>
          <div className="mac-buttons">
            <div className="mac-btn mac-close" onClick={(e) => { e.stopPropagation(); onClose(); }}></div>
            <div className="mac-btn mac-minimize" onClick={(e) => { e.stopPropagation(); onMinimize(); }}></div>
            <div className="mac-btn mac-maximize"></div>
          </div>
          <div className="chat-title">
            {isChannel ? (
              <FontAwesomeIcon icon={faHashtag} className="me-1" />
            ) : (
              <span className="me-1">💬</span>
            )}
            {channelName}
            {ircConnected && (
              <FontAwesomeIcon icon={faCircle} className="ms-2" style={{ fontSize: '6px', color: '#34C759' }} />
            )}
            {/* Indicador de privilegio */}
            {isChannel && amIOperator && (
              <span className="privilege-indicator" title={`Eres ${myPrivilege === 'owner' ? 'Owner' : 'Operador'}`}>
                {myPrivilege === 'owner' ? '👑' : '🛡️'}
              </span>
            )}
          </div>
          <div className="chat-title-actions">
            {isChannel && (
              <span className="chat-users-count">
                <FontAwesomeIcon icon={faUsers} className="me-1" />
                {channelUsersList.length}
              </span>
            )}
            {/* Toggle panel solo para canales */}
            {isChannel && (
              <button 
                className="panel-toggle-btn"
                onClick={(e) => { e.stopPropagation(); setShowAudiencePanel(!showAudiencePanel); }}
                title={showAudiencePanel ? 'Ocultar panel de audiencia' : 'Mostrar panel de audiencia'}
              >
                {showAudiencePanel ? '📊' : '👁️'}
              </button>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            MAIN CONTENT (Chat + Panel)
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="chat-main-container">
          {/* CHAT AREA */}
          <div className="chat-area" style={{ 
            flex: (isChannel && showAudiencePanel) ? `1 1 ${win.size.width - audiencePanelWidth - 4}px` : '1 1 100%' 
          }}>
            <div className="chat-messages-aqua" ref={messagesContainerRef}>
              {channelMessages.length === 0 && !ircConnected && (
                <>
                  <div className="chat-msg">
                    <span className="msg-time">[--:--]</span>
                    <span className="msg-nick" style={{ color: '#0066cc' }}>~System:</span>
                    <span className="msg-text">Bienvenido a {channelName}</span>
                  </div>
                  <div className="chat-hint">
                    <FontAwesomeIcon icon={faFire} className="me-2" style={{ color: '#ff9500' }} />
                      Conecta a la red LinkHub!
                  </div>
                </>
              )}
              
              {channelMessages.length === 0 && ircConnected && (
                <div className="chat-msg">
                  <span className="msg-time">[{new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}]</span>
                  <span className="msg-nick" style={{ color: '#34C759' }}>~System:</span>
                  <span className="msg-text">Conectado a {channelName} ✨</span>
                </div>
              )}
              
              {channelMessages.map((msg, idx) => {
                const time = new Date(msg.timestamp).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
                const nick = msg.from || msg.sender || 'System';
                const isCommand = msg.metadata?.isCommandFeedback; // ✅ NUEVO
                const commandType = msg.metadata?.commandType; // ✅ NUEVO
  
                return (
                  <div 
                      key={msg.id || idx} 
                      className={`chat-msg ${isCommand ? `command-feedback ${commandType}` : ''}`} // ✅ NUEVO
                    >
                    <span className="msg-time">[{time}]</span>
                    {!isCommand && ( // ✅ NUEVO: No mostrar nick en comandos
                      <span className="msg-nick" style={{ color: '#0066cc' }}>{nick}:</span>
                    )}
                    <span className="msg-text">{msg.text}</span>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input-aqua">
              <InputGroup size="sm">
                <Form.Control
                  ref={inputRef}
                  type="text"
                  placeholder={ircConnected ? `Escribir en ${channelName}...` : "Conecta primero a LinkHub..."}
                  defaultValue=""
                  onChange={handleInputChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleLocalCommand()}
                  className="chat-input-field"
                  disabled={!ircConnected}
                  autoFocus
                />
                <Button variant="primary" onClick={handleLocalCommand} className="chat-send-btn">
                  <FontAwesomeIcon icon={faMessage} />
                </Button>
              </InputGroup>
            </div>
          </div>

          {/* DIVISOR HORIZONTAL (drag para resize panel) - SOLO CANALES */}
          {isChannel && showAudiencePanel && (
            <div 
              className="panel-divider-horizontal"
              onMouseDown={handlePanelDividerDrag}
              title="Arrastra para redimensionar"
            />
          )}

          {/* ═══════════════════════════════════════════════════════════════════
              AUDIENCE PANEL (SOLO CANALES)
              ═══════════════════════════════════════════════════════════════════ */}
          {isChannel && showAudiencePanel && (
            <div className="audience-panel" style={{ width: audiencePanelWidth }}>
              {/* GRAFO DE AUDIENCIA */}
              <div className="audience-graph-section" style={{ height: `${graphHeightPercent}%` }}>
                <div className="section-header-mini">
                  <span>🌐 Audiencia</span>
                  <span className="section-badge">{channelUsersList.length}</span>
                </div>
                <div className="audience-graph-container">
                  {/* Grafo visual de nodos */}
                  {channelUsersList.length === 0 ? (
                    <div className="graph-empty-state">
                      <span>🔌</span>
                      <small>Sin usuarios</small>
                    </div>
                  ) : (
                    <svg className="audience-graph-svg" viewBox="0 0 200 150">
                      {/* Líneas de conexión (simplificadas por ahora) */}
                      {channelUsersList.length > 1 && channelUsersList.slice(0, 8).map((user, idx) => {
                        if (idx === 0) return null;
                        const prevIdx = Math.floor(Math.random() * idx);
                        const x1 = 100 + Math.cos((idx / channelUsersList.length) * Math.PI * 2) * 50;
                        const y1 = 75 + Math.sin((idx / channelUsersList.length) * Math.PI * 2) * 40;
                        const x2 = 100 + Math.cos((prevIdx / channelUsersList.length) * Math.PI * 2) * 50;
                        const y2 = 75 + Math.sin((prevIdx / channelUsersList.length) * Math.PI * 2) * 40;
                        return (
                          <line 
                            key={`line-${idx}`}
                            x1={x1} y1={y1} x2={x2} y2={y2}
                            stroke="rgba(0, 212, 255, 0.3)"
                            strokeWidth="1"
                          />
                        );
                      })}
                      
                      {/* Nodos de usuarios */}
                      {channelUsersList.slice(0, 8).map((user, idx) => {
                        const angle = (idx / Math.min(channelUsersList.length, 8)) * Math.PI * 2;
                        const radius = 50;
                        const cx = 100 + Math.cos(angle) * radius;
                        const cy = 75 + Math.sin(angle) * radius;
                        const privInfo = getUserPrivilegeInfo(user.privilege);
                        
                        return (
                          <g key={user.nick} className="graph-node-group">
                            {/* Círculo del nodo */}
                            <circle
                              cx={cx}
                              cy={cy}
                              r={user.privilege === 'owner' ? 12 : user.privilege === 'op' ? 10 : 8}
                              fill={privInfo.color}
                              className="graph-node"
                              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
                            />
                            {/* Emoji de privilegio */}
                            {privInfo.icon && (
                              <text
                                x={cx}
                                y={cy + 3}
                                textAnchor="middle"
                                fontSize="8"
                                className="node-icon"
                              >
                                {privInfo.icon}
                              </text>
                            )}
                            {/* Nombre (solo primeros 6 chars) */}
                            <text
                              x={cx}
                              y={cy + (user.privilege === 'owner' ? 22 : 18)}
                              textAnchor="middle"
                              fontSize="7"
                              fill="#666"
                              className="node-label"
                            >
                              {user.nick.slice(0, 6)}
                            </text>
                          </g>
                        );
                      })}
                      
                      {/* Indicador de más usuarios */}
                      {channelUsersList.length > 8 && (
                        <text x="100" y="145" textAnchor="middle" fontSize="9" fill="#888">
                          +{channelUsersList.length - 8} más
                        </text>
                      )}
                    </svg>
                  )}
                </div>
              </div>

              {/* DIVISOR VERTICAL (drag para resize grafo/userlist) */}
              <div 
                className="panel-divider-vertical"
                onMouseDown={handleGraphDividerDrag}
                title="Arrastra para redimensionar"
              />

              {/* USERLIST IRC */}
              <div className="audience-userlist-section" style={{ height: `${100 - graphHeightPercent}%` }}>
                <div className="section-header-mini">
                  <span>👥 Usuarios</span>
                  <span className="section-badge">{channelUsersList.length}</span>
                </div>
                <div className="userlist-container">
                  {channelUsersList.length === 0 ? (
                    <div className="userlist-empty">
                      <small>Sin usuarios en el canal</small>
                    </div>
                  ) : (
                    channelUsersList.map((user) => {
                      const privInfo = getUserPrivilegeInfo(user.privilege);
                      const isMe = user.nick.toLowerCase() === myNickname.toLowerCase();
                      
                      return (
                        <div 
                          key={user.nick} 
                          className={`userlist-item ${isMe ? 'userlist-item-me' : ''}`}
                          title={isMe 
                            ? `${user.nick} (Tú) - ${user.host}`
                            : `${user.nick} - ${user.host}\n🖱️ Doble clic: Abrir privado\n🖱️ Click derecho: Opciones`
                          }
                          onDoubleClick={() => handleUserDoubleClick(user)}
                          onContextMenu={(e) => handleUserContextMenu(e, user)}
                        >
                          <span className="user-privilege" style={{ color: isMe ? '#007AFF' : privInfo.color }}>
                            {privInfo.icon || '•'}
                          </span>
                          <span className="user-prefix" style={{ color: isMe ? '#007AFF' : privInfo.color }}>
                            {privInfo.prefix}
                          </span>
                          <span className={`user-nick ${isMe ? 'user-nick-me' : ''}`}>
                            {user.nick}
                            {isMe && <span className="me-badge">Tú</span>}
                          </span>
                          <span className="user-status">{isMe ? '⭐' : '🟢'}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════
            MENÚ CONTEXTUAL DE USUARIO (SOLO EN CANALES)
            ═══════════════════════════════════════════════════════════════════ */}
        {userContextMenu && isChannel && (() => {
          const isTargetMe = userContextMenu.user.nick.toLowerCase() === myNickname.toLowerCase();
          
          return (
          <div
            className="user-context-menu"
            style={{
              position: 'absolute',  // ✅ FIX: Cambiado de 'fixed' a 'absolute' para moverse con la ventana
              left: userContextMenu.x,
              top: userContextMenu.y,
              zIndex: 10000000  // ✅ Muy alto para estar sobre ventanas flotantes (100000+)
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="context-menu-header">
              <span className="context-user-icon">
                {getUserPrivilegeInfo(userContextMenu.user.privilege).icon || '👤'}
              </span>
              <span className="context-user-nick">
                {userContextMenu.user.nick}
                {isTargetMe && <span className="context-me-indicator">(Tú)</span>}
              </span>
              {amIOperator && (
                <span className="context-my-status">🛡️ Eres OP</span>
              )}
            </div>
            <div className="context-menu-divider" />
            
            {/* ═══════════════════════════════════════════════════════════════════
                ACCIONES BÁSICAS (cualquier usuario puede hacer)
                ═══════════════════════════════════════════════════════════════════ */}
            {/* ✅ Ocultar "Abrir Privado" si es mi propio nick */}
            {!isTargetMe && (
              <div className="context-menu-item" onClick={() => handleUserAction('query', userContextMenu.user)}>
                <span className="context-item-icon">💬</span>
                <span>Abrir Privado</span>
              </div>
            )}
            {isTargetMe && (
              <div className="context-menu-item context-item-disabled">
                <span className="context-item-icon">👤</span>
                <span>Este eres tú</span>
              </div>
            )}
            <div className="context-menu-item" onClick={() => handleUserAction('whois', userContextMenu.user)}>
              <span className="context-item-icon">🔍</span>
              <span>Whois</span>
            </div>
            
            {/* ═══════════════════════════════════════════════════════════════════
                ACCIONES DE OPERADOR DE CANAL (solo si soy @op)
                ═══════════════════════════════════════════════════════════════════ */}
            {amIOperator && (
              <>
                <div className="context-menu-divider" />
                <div className="context-menu-section">🛡️ Operador de Canal</div>
                
                <div className="context-menu-item" onClick={() => handleUserAction('op', userContextMenu.user)}>
                  <span className="context-item-icon">🛡️</span>
                  <span>Dar OP (+o)</span>
                </div>
                <div className="context-menu-item" onClick={() => handleUserAction('deop', userContextMenu.user)}>
                  <span className="context-item-icon">🚫</span>
                  <span>Quitar OP (-o)</span>
                </div>
                <div className="context-menu-item" onClick={() => handleUserAction('voice', userContextMenu.user)}>
                  <span className="context-item-icon">🎤</span>
                  <span>Dar Voice (+v)</span>
                </div>
                <div className="context-menu-item" onClick={() => handleUserAction('devoice', userContextMenu.user)}>
                  <span className="context-item-icon">🔇</span>
                  <span>Quitar Voice (-v)</span>
                </div>
                
                <div className="context-menu-divider" />
                <div className="context-menu-section">⚠️ Moderación</div>
                
                <div className="context-menu-item context-item-warning" onClick={() => handleUserAction('kick', userContextMenu.user)}>
                  <span className="context-item-icon">👢</span>
                  <span>Kick</span>
                </div>
                <div className="context-menu-item context-item-warning" onClick={() => handleUserAction('ban', userContextMenu.user)}>
                  <span className="context-item-icon">🚷</span>
                  <span>Ban (+b)</span>
                </div>
                <div className="context-menu-item context-item-warning" onClick={() => handleUserAction('unban', userContextMenu.user)}>
                  <span className="context-item-icon">✅</span>
                  <span>Unban (-b)</span>
                </div>
                <div className="context-menu-item context-item-danger" onClick={() => handleUserAction('kickban', userContextMenu.user)}>
                  <span className="context-item-icon">💀</span>
                  <span>Kick + Ban</span>
                </div>
              </>
            )}
            
            {/* ═══════════════════════════════════════════════════════════════════
                ACCIONES IRCOP (operador de red) - siempre visible para testing
                En producción: detectar si el usuario tiene +o global
                ═══════════════════════════════════════════════════════════════════ */}
            <div className="context-menu-divider" />
            <div className="context-menu-section">🌐 IRCop (Red)</div>
            
            <div className="context-menu-item context-item-danger" onClick={() => handleUserAction('kill', userContextMenu.user)}>
              <span className="context-item-icon">⚡</span>
              <span>KILL (desconectar)</span>
            </div>
            <div className="context-menu-item context-item-danger" onClick={() => handleUserAction('kline', userContextMenu.user)}>
              <span className="context-item-icon">🔒</span>
              <span>K-Line (ban servidor)</span>
            </div>
            <div className="context-menu-item context-item-danger" onClick={() => handleUserAction('gline', userContextMenu.user)}>
              <span className="context-item-icon">🌍</span>
              <span>G-Line (ban global)</span>
            </div>
            <div className="context-menu-item context-item-danger" onClick={() => handleUserAction('zline', userContextMenu.user)}>
              <span className="context-item-icon">🚫</span>
              <span>Z-Line (ban IP)</span>
            </div>
            <div className="context-menu-item context-item-warning" onClick={() => handleUserAction('shun', userContextMenu.user)}>
              <span className="context-item-icon">🔇</span>
              <span>SHUN (silenciar)</span>
            </div>
            <div className="context-menu-item" onClick={() => handleUserAction('sajoin', userContextMenu.user)}>
              <span className="context-item-icon">➡️</span>
              <span>SA-JOIN (forzar entrada)</span>
            </div>
            <div className="context-menu-item" onClick={() => handleUserAction('sapart', userContextMenu.user)}>
              <span className="context-item-icon">⬅️</span>
              <span>SA-PART (forzar salida)</span>
            </div>
          </div>
        );})()}
      </div>
    );
  };

  const renderCommunities = () => (
    <div className="communities-section">
      <div className="section-header">
        <h5>Comunidades Activas</h5>
        <div style={{ display: 'flex', gap: '12px' }}>
          {ircConnected ? (
            <>
              <Button variant="outline-secondary" size="sm" onClick={refreshChannels}>
                <FontAwesomeIcon icon={faCircle} className="me-2" style={{ fontSize: '8px' }} />
                Refrescar
              </Button>
              <Button variant="success" size="sm" onClick={handleIrcDisconnect}>
                <FontAwesomeIcon icon={faCircle} className="me-2" style={{ fontSize: '8px' }} />
                Conectado al Servidor
              </Button>
            </>
          ) : (
            <Button variant="outline-primary" size="sm" onClick={() => setShowIrcConfig(true)}>
              <FontAwesomeIcon icon={faServer} className="me-2" />
              Conectar a LinkHub
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={() => setShowJoinChannelModal(true)}>
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Unirse a Canal
          </Button>
        </div>
      </div>

      <div className="communities-grid">
        {communities.map(comm => (
          <Card 
            key={comm.id} 
            className="community-card-social"
            onClick={() => openChatWindow(comm.name, 'channel')}
            style={{ cursor: 'pointer' }}
          >
            {/* Banner/Avatar Section */}
            <div className="community-visual">
              <div className="community-avatar-wrapper">
                <img 
                  src={comm.avatar} 
                  alt={comm.name}
                  className="community-avatar"
                  onError={(e) => {
                    e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comm.name)}&background=007AFF&color=fff&size=120`;
                  }}
                />
                {comm.verified && (
                  <div className="verified-badge" title="Comunidad Verificada">
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                )}
              </div>
            </div>

            <Card.Body className="community-card-body">
              {/* Header: Name + Status */}
              <div className="community-header-social">
                <div className="community-name-wrapper">
                  <h6 className="community-name-social">{comm.name}</h6>
                  {comm.trending && (
                    <Badge bg="danger" className="trending-badge">
                      <FontAwesomeIcon icon={faFire} className="me-1" />
                      Trending
                    </Badge>
                  )}
                </div>
                <Badge 
                  className="status-badge-social"
                  style={{ 
                    background: getStatusColor(comm.status),
                    fontSize: '9px',
                    padding: '4px 8px'
                  }}
                >
                  {comm.status === 'hot' ? '🔥 Muy activo' : 
                   comm.status === 'active' ? '💚 Activo' : 
                   '💤 Tranquilo'}
                </Badge>
              </div>

              {/* Category + Stats */}
              <div className="community-meta">
                <span className="community-category">
                  {comm.category}
                </span>
                <span className="community-stats-inline">
                  <FontAwesomeIcon icon={faUsers} className="me-1" />
                  {comm.users > 1000 ? `${(comm.users / 1000).toFixed(1)}K` : comm.users} miembros
                </span>
              </div>

              {/* Description */}
              <div className="community-description-social">
                {comm.description}
              </div>

              {/* Activity Graph */}
              <div className="community-activity-social">
                <div className="activity-label-social">Actividad (7 días)</div>
                <MiniSparkline data={comm.activity} />
              </div>

              {/* Last Message Feed */}
              <div className="community-feed">
                <div className="feed-header">
                  <FontAwesomeIcon icon={faComments} className="me-1" />
                  Conversación reciente
                </div>
                <div className="feed-message">
                  <span className="feed-user">@{comm.lastMessage.user}:</span>
                  <span className="feed-text">{comm.lastMessage.text.substring(0, 80)}{comm.lastMessage.text.length > 80 ? '...' : ''}</span>
                </div>
                <div className="feed-time">
                  <FontAwesomeIcon icon={faClock} className="me-1" />
                  {comm.lastMessage.time}
                </div>
              </div>

              {/* Action Button */}
              <Button 
                variant="primary" 
                size="sm" 
                className="join-community-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  openChatWindow(comm.name, 'channel');
                }}
              >
                <FontAwesomeIcon icon={faComments} className="me-2" />
                Unirse a la conversación
              </Button>
            </Card.Body>
          </Card>
        ))}

        <Card className="community-card add-card" onClick={() => setShowJoinChannelModal(true)}>
          <Card.Body className="add-card-body">
            <FontAwesomeIcon icon={faPlus} className="add-icon" />
            <div className="add-text">Agregar Comunidad</div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );

  const renderUsers = () => {
    // ═══════════════════════════════════════════════════════════════════════════
    // GENERAR LISTA DE USUARIOS DESDE IRC REAL (no mock)
    // ═══════════════════════════════════════════════════════════════════════════
    let allUsers: User[] = [];
    
    if (ircConnected && ircUsers.size > 0) {
      // Convertir Map<channel, IRCUser[]> a User[]
      const usersMap = new Map<string, User>();
      
      for (const [channel, channelUsers] of ircUsers) {
        for (const ircUser of channelUsers) {
          const existingUser = usersMap.get(ircUser.nick);
          
          if (existingUser) {
            // Agregar canal si no está
            if (!existingUser.channels.includes(channel)) {
              existingUser.channels.push(channel);
            }
          } else {
            // Determinar privilegio basado en modes
            let privilege: 'owner' | 'op' | 'voice' | 'user' = 'user';
            if (ircUser.privilege === 'owner' || (ircUser as any).modes?.includes('q')) {
              privilege = 'owner';
            } else if (ircUser.privilege === 'op' || (ircUser as any).modes?.includes('o')) {
              privilege = 'op';
            } else if (ircUser.privilege === 'voice' || (ircUser as any).modes?.includes('v')) {
              privilege = 'voice';
            }
            
            usersMap.set(ircUser.nick, {
              id: ircUser.nick,
              nick: ircUser.nick,
              host: ircUser.host || 'unknown',
              privilege,
              channels: [channel],
              lastSeen: ircUser.idle ? `${Math.floor(ircUser.idle / 60)}m` : 'now',
              messages: 0 // TODO: Contar desde ircMessages
            });
          }
        }
      }
      
      allUsers = Array.from(usersMap.values());
    } else {
      // Fallback a mock si no hay conexión
      allUsers = mockUsers;
    }
    
    const filteredUsers = allUsers.filter(u => 
      u.nick.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.host.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalUsers = filteredUsers.length;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginatedUsers = filteredUsers.slice(startIndex, startIndex + itemsPerPage);

    const statsValues = {
      total: ircStats?.total_users || allUsers.length,
      online: ircConnected ? allUsers.length : 0,
      operators: allUsers.filter(u => u.privilege === 'op' || u.privilege === 'owner').length
    };

    return (
      <div className="users-section">
        <div className="stats-cards-row">
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-card-content">
                <FontAwesomeIcon icon={faUsers} className="stat-card-icon" style={{ color: '#007AFF' }} />
                <div className="stat-card-info">
                  <div className="stat-card-value">{totalUsers}</div>
                  <div className="stat-card-label">Total Usuarios</div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="stat-card">
            <Card.Body>
              <div className="stat-card-content">
                <FontAwesomeIcon icon={faCircle} className="stat-card-icon" style={{ color: '#34C759' }} />
                <div className="stat-card-info">
                  <div className="stat-card-value">{statsValues.online}</div>
                  <div className="stat-card-label">Online</div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="stat-card">
            <Card.Body>
              <div className="stat-card-content">
                <FontAwesomeIcon icon={faCrown} className="stat-card-icon" style={{ color: '#FF9500' }} />
                <div className="stat-card-info">
                  <div className="stat-card-value">{statsValues.operators}</div>
                  <div className="stat-card-label">Operadores</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="table-section">
          <div className="table-header">
            <h5>Listado de Usuarios</h5>
            <InputGroup style={{ maxWidth: '300px' }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar usuario..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          <Table hover className="custom-table">
            <thead>
              <tr>
                <th>Nickname</th>
                <th>Hostname</th>
                <th>Privilegio</th>
                <th>Comunidades</th>
                <th>Mensajes</th>
                <th>Última vez</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map(user => {
                const privInfo = getPrivilegeIcon(user.privilege);
                return (
                  <tr key={user.id}>
                    <td>
                      <div className="user-cell">
                        <FontAwesomeIcon icon={privInfo.icon} style={{ color: privInfo.color, marginRight: '8px' }} />
                        <strong>{user.nick}</strong>
                      </div>
                    </td>
                    <td><code>{user.host}</code></td>
                    <td>
                      <Badge bg="secondary" className="privilege-badge">
                        {user.privilege}
                      </Badge>
                    </td>
                    <td>{user.channels.join(', ')}</td>
                    <td>{user.messages}</td>
                    <td>{user.lastSeen}</td>
                    <td>
                      <div className="action-buttons">
                        <Button variant="outline-primary" size="sm">
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button variant="outline-danger" size="sm">
                          <FontAwesomeIcon icon={faBan} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="pagination-wrapper">
            <Pagination size="sm">
              <Pagination.Prev 
                disabled={currentPage === 1} 
                onClick={() => setCurrentPage(currentPage - 1)} 
              />
              <Pagination.Item active>{currentPage}</Pagination.Item>
              <Pagination.Item onClick={() => setCurrentPage(currentPage + 1)}>
                {currentPage + 1}
              </Pagination.Item>
              <Pagination.Next 
                disabled={startIndex + itemsPerPage >= totalUsers} 
                onClick={() => setCurrentPage(currentPage + 1)} 
              />
            </Pagination>
          </div>
        </div>
      </div>
    );
  };

  const renderChannels = () => {
    const filteredChannels = channels.filter(c => 
      c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const displayedChannelCards = channels.slice(0, channelCardsCount);

    const statsValues = {
      total: ircChannels.length || 12,
      active: ircChannels.filter(ch => ch.users > 0).length || 8,
      totalUsers: ircStats?.total_users || 374
    };

    return (
      <div className="channels-section">
        <div className="stats-cards-row">
          <Card className="stat-card">
            <Card.Body>
              <div className="stat-card-content">
                <FontAwesomeIcon icon={faHashtag} className="stat-card-icon" style={{ color: '#007AFF' }} />
                <div className="stat-card-info">
                  <div className="stat-card-value">{statsValues.total}</div>
                  <div className="stat-card-label">Total Comunidades</div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="stat-card">
            <Card.Body>
              <div className="stat-card-content">
                <FontAwesomeIcon icon={faCircle} className="stat-card-icon" style={{ color: '#34C759' }} />
                <div className="stat-card-info">
                  <div className="stat-card-value">{statsValues.active}</div>
                  <div className="stat-card-label">Activos</div>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="stat-card">
            <Card.Body>
              <div className="stat-card-content">
                <FontAwesomeIcon icon={faUsers} className="stat-card-icon" style={{ color: '#FF9500' }} />
                <div className="stat-card-info">
                  <div className="stat-card-value">{statsValues.totalUsers}</div>
                  <div className="stat-card-label">Usuarios Total</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="channel-cards-section">
          <div className="channel-cards-header">
            <h5>Comunidades Destacadas</h5>
            <div className="cards-filter">
              <span className="filter-label">Mostrar:</span>
              <ButtonGroup size="sm">
                <Button 
                  variant={channelCardsCount === 4 ? 'primary' : 'outline-secondary'}
                  onClick={() => setChannelCardsCount(4)}
                >
                  4
                </Button>
                <Button 
                  variant={channelCardsCount === 6 ? 'primary' : 'outline-secondary'}
                  onClick={() => setChannelCardsCount(6)}
                >
                  6
                </Button>
                <Button 
                  variant={channelCardsCount === 8 ? 'primary' : 'outline-secondary'}
                  onClick={() => setChannelCardsCount(8)}
                >
                  8
                </Button>
                <Button 
                  variant={channelCardsCount === 12 ? 'primary' : 'outline-secondary'}
                  onClick={() => setChannelCardsCount(12)}
                >
                  12
                </Button>
              </ButtonGroup>
            </div>
          </div>

          <div className="channel-cards-grid">
            {displayedChannelCards.map(channel => (
              <Card key={channel.id} className="channel-mini-card" onClick={() => openChatWindow(channel.name)}>
                <Card.Body>
                  <div className="channel-mini-header">
                    <h6>{channel.name}</h6>
                    <Badge bg="success" style={{ fontSize: '9px' }}>
                      <FontAwesomeIcon icon={faCircle} style={{ fontSize: '6px', marginRight: '4px' }} />
                      LIVE
                    </Badge>
                  </div>
                  <div className="channel-mini-topic">{channel.topic}</div>
                  <div className="channel-mini-stats">
                    <div className="mini-stat">
                      <FontAwesomeIcon icon={faUsers} />
                      <span>{channel.users}</span>
                    </div>
                    <div className="mini-stat">
                      <FontAwesomeIcon icon={faComments} />
                      <span>{channel.messages}</span>
                    </div>
                  </div>
                  <div className="channel-mini-mode">
                    <code>{channel.mode}</code>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        </div>

        <div className="table-section">
          <div className="table-header">
            <h5>Listado de Comunidades</h5>
            <InputGroup style={{ maxWidth: '300px' }}>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Buscar canal..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          <Table hover className="custom-table">
            <thead>
              <tr>
                <th>Canal</th>
                <th>Topic</th>
                <th>Usuarios</th>
                <th>Mensajes</th>
                <th>Modo</th>
                <th>Creado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredChannels.map(channel => (
                <tr key={channel.id}>
                  <td><strong>{channel.name}</strong></td>
                  <td>{channel.topic}</td>
                  <td>{channel.users}</td>
                  <td>{channel.messages}</td>
                  <td><code>{channel.mode}</code></td>
                  <td>{channel.created}</td>
                  <td>
                    <div className="action-buttons">
                      <Button variant="outline-primary" size="sm" onClick={() => openChatWindow(channel.name)}>
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handlePartChannel(channel.name)}>
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  const renderMirc32 = () => {
    const themes = [
      { id: 'classic', name: 'Classic mIRC', bg: '#ffffff', text: '#000000' },
      { id: 'dark', name: 'Dark Steel', bg: '#1e1e1e', text: '#d4d4d4' },
      { id: 'matrix', name: 'Matrix', bg: '#000000', text: '#00ff00' },
      { id: 'sunset', name: 'Sunset', bg: '#2d1b2e', text: '#ff6b9d' }
    ];

    const currentTheme = themes.find(t => t.id === selectedTheme) || themes[0];

    // Usar canales reales si está conectado, sino mostrar mock
    const displayChannels = ircConnected && ircChannels.length > 0 
      ? ircChannels 
      : [
          { name: '#linkhub', users: 0, unread: 0 },
          { name: '#general', users: 0, unread: 0 }
        ];

    // Obtener usuarios del canal activo
    const activeChannelUsers = ircUsers.get(mircActiveChannel) || [
      { nick: 'System', host: 'localhost', privilege: 'user' as const, idle: 0 }
    ];

    // ✅ DETECTAR MI PRIVILEGIO EN ESTE CANAL (mIRC32)
    const myNickname = ircConfig.nickname;
    
    // ✅ Buscar mi usuario en la lista (comparación case-insensitive)
    const myUserData = activeChannelUsers.find(u => 
      u.nick.toLowerCase() === myNickname.toLowerCase()
    );
    const myPrivilege = myUserData?.privilege || 'user';
    const amIOperator = myPrivilege === 'owner' || myPrivilege === 'op';
    
    console.log(`🔍 [mIRC32] Canal: ${mircActiveChannel}, Mi privilegio: ${myPrivilege}, ¿Soy operador?: ${amIOperator}`);

    // Obtener mensajes del canal activo
    const activeChannelMessages = ircMessages.get(mircActiveChannel) || [];

    const handleResizeStart = (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = channelPanelWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        const newWidth = Math.max(150, Math.min(400, startWidth + delta));
        setChannelPanelWidth(newWidth);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    };

    const handleUserRightClick = (e: React.MouseEvent, user: IRCUser) => {
      e.preventDefault();
      e.stopPropagation();
      
      // ✅ Dimensiones del menú
      const menuWidth = 260;
      const menuHeight = 450;
      const padding = 10;
      
      // ✅ Obtener límites del contenedor mIRC32
      const container = mircContainerRef.current;
      if (!container) {
        console.warn('⚠️ No hay ref al contenedor mIRC32');
        setContextMenu({ x: e.clientX, y: e.clientY, user });
        return;
      }
      
      const containerRect = container.getBoundingClientRect();
      
      // ✅ CONVERTIR coordenadas GLOBALES → RELATIVAS al contenedor
      const clickXGlobal = e.clientX;
      const clickYGlobal = e.clientY;
      const clickXRelative = clickXGlobal - containerRect.left;
      const clickYRelative = clickYGlobal - containerRect.top;
      
      console.log('🔍 DEBUG MENU CONTEXTUAL:');
      console.log('  Click global:', clickXGlobal, clickYGlobal);
      console.log('  Click relativo al contenedor:', clickXRelative, clickYRelative);
      console.log('  Container:', {
        left: containerRect.left,
        top: containerRect.top,
        width: containerRect.width,
        height: containerRect.height
      });
      console.log('  Menu size:', menuWidth, menuHeight);
      
      // Dimensiones del contenedor (límites RELATIVOS)
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      // Calcular posición inicial (derecha del click, abajo del click)
      let x = clickXRelative;
      let y = clickYRelative;
      
      // ✅ HORIZONTAL: ¿Cabe a la derecha dentro del contenedor?
      const spaceRight = containerWidth - clickXRelative;
      if (spaceRight < menuWidth) {
        // No cabe a la derecha → abrir a la izquierda del click
        x = clickXRelative - menuWidth;
        console.log('  → Abriendo a la IZQUIERDA (espacio derecho:', spaceRight, 'px)');
      } else {
        console.log('  → Abriendo a la DERECHA (espacio:', spaceRight, 'px)');
      }
      
      // ✅ VERTICAL: ¿Cabe abajo dentro del contenedor?
      const spaceBottom = containerHeight - clickYRelative;
      if (spaceBottom < menuHeight) {
        // No cabe abajo → abrir hacia arriba del click
        y = clickYRelative - menuHeight;
        console.log('  → Abriendo ARRIBA (espacio abajo:', spaceBottom, 'px)');
      } else {
        console.log('  → Abriendo ABAJO (espacio:', spaceBottom, 'px)');
      }
      
      // ✅ Forzar dentro de los límites del contenedor
      const finalX = Math.max(padding, Math.min(x, containerWidth - menuWidth - padding));
      const finalY = Math.max(padding, Math.min(y, containerHeight - menuHeight - padding));
      
      console.log('  Posición calculada (relativa):', x, y);
      console.log('  Posición final (limitada):', finalX, finalY);
      
      setContextMenu({ x: finalX, y: finalY, user });
    };

  const handleUserAction = async (action: string, user: IRCUser) => {
    console.log(`🎯 Acción: ${action} sobre ${user.nick}`);
    setContextMenu(null);
  
    if (!ircConnected) {
      showNotification('warning', 'Debes estar conectado al servidor para realizar esta acción');
      return;
    }

  const channel = mircActiveChannel; // ✅ Capturar canal activo

  // ✅ Helper para ejecutar comandos IRC
  const executeIrcCommand = async (cmd: string, displayText?: string) => {
    // Parsear comando: /whois Paula → command="WHOIS", args=["Paula"]
    const parts = cmd.slice(1).split(' ').filter(p => p.trim());
    if (parts.length === 0) return;
    
    const command = parts[0].toUpperCase();
    const args = parts.slice(1);
    
    try {
      await linkHubIrcService.executeCommand(command, args);
      addCommandFeedback(channel, displayText || `→ ${command} ${args.join(' ')}`, 'sent');
    } catch (error: any) {
      console.error(`❌ Error ejecutando ${command}:`, error);
      addCommandFeedback(channel, `❌ Error: ${error.message}`, 'error');
    }
  };

  switch (action) {
    case 'whois':
      await executeIrcCommand(`/whois ${user.nick}`, `→ WHOIS ${user.nick}`);
      break;
      
    case 'query':
      openChatWindow(user.nick, 'private');
      break;
      
    case 'op':
      await executeIrcCommand(`/mode ${mircActiveChannel} +o ${user.nick}`, `→ Dando OP a ${user.nick}`);
      showNotification('success', `OP dado a ${user.nick}`);
      break;
      
    case 'deop':
      await executeIrcCommand(`/mode ${mircActiveChannel} -o ${user.nick}`, `→ Quitando OP a ${user.nick}`);
      showNotification('success', `OP removido de ${user.nick}`);
      break;
      
    case 'voice':
      await executeIrcCommand(`/mode ${mircActiveChannel} +v ${user.nick}`, `→ Dando Voice a ${user.nick}`);
      showNotification('success', `Voice dado a ${user.nick}`);
      break;
      
    case 'devoice':
      await executeIrcCommand(`/mode ${mircActiveChannel} -v ${user.nick}`, `→ Quitando Voice a ${user.nick}`);
      showNotification('success', `Voice removido de ${user.nick}`);
      break;
      
    case 'kick':
      showActionPrompt(
        `Expulsar a ${user.nick}`,
        'Razón del kick',
        'Kick por el operador',
        'Ingresa la razón del kick...',
        async (kickReason) => {
          await executeIrcCommand(`/kick ${mircActiveChannel} ${user.nick} ${kickReason}`, `→ KICK ${user.nick}: ${kickReason}`);
          showNotification('success', `${user.nick} fue expulsado del canal`);
        }
      );
      break;
      
    case 'ban':
      const banMask = user.host ? `*!*@${user.host.split('@').pop()}` : user.nick;
      await executeIrcCommand(`/mode ${mircActiveChannel} +b ${banMask}`, `→ BAN ${banMask}`);
      showNotification('success', `${user.nick} fue baneado`, `Máscara: ${banMask}`);
      break;
      
    case 'unban':
      showActionPrompt(
        `Desbanear a ${user.nick}`,
        'Máscara a desbanear',
        user.host ? `*!*@${user.host.split('@').pop()}` : user.nick,
        'Ingresa la máscara (ej: *!*@host.com)...',
        async (unbanMask) => {
          await executeIrcCommand(`/mode ${mircActiveChannel} -b ${unbanMask}`, `→ UNBAN ${unbanMask}`);
          showNotification('success', 'Ban removido', `Máscara: ${unbanMask}`);
        }
      );
      break;
      
    case 'kickban':
      showActionPrompt(
        `Kickban a ${user.nick}`,
        'Razón del kickban',
        'Violación de reglas',
        'Ingresa la razón...',
        async (kbReason) => {
          const kbMask = user.host ? `*!*@${user.host.split('@').pop()}` : user.nick;
          await executeIrcCommand(`/mode ${mircActiveChannel} +b ${kbMask}`, `→ BAN ${kbMask}`);
          await executeIrcCommand(`/kick ${mircActiveChannel} ${user.nick} ${kbReason}`, `→ KICK ${user.nick}: ${kbReason}`);
          showNotification('success', `${user.nick} fue expulsado y baneado`);
        }
      );
      break;
      
    default:
      console.warn('Acción desconocida:', action);
  }
};

    const handleUserDoubleClick = (user: IRCUser) => {
      // ✅ Bloquear si es mi propio nick
      if (user.nick.toLowerCase() === ircConfig.nickname.toLowerCase()) {
        console.log(`🚫 No puedes abrir privado contigo mismo`);
        return;
      }
      openChatWindow(user.nick, 'private');
    };

    return (
      <div 
        className="mirc32-container" 
        ref={mircContainerRef}
        style={{ 
          background: mircDarkMode ? '#2b2b2b' : currentTheme.bg,
          color: mircDarkMode ? '#d4d4d4' : currentTheme.text
        }}
      >
        <div className="mirc32-toolbar">
          <div className="mirc32-title">
            <img src={mirclogo} width={24}></img> {mircActiveChannel}@lhp://linkhub.network
          </div>
          <div className="mirc32-controls">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setMircDarkMode(!mircDarkMode)}
              title={mircDarkMode ? 'Light Mode' : 'Dark Mode'}
            >
              <FontAwesomeIcon icon={mircDarkMode ? faSun : faMoon} />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={!themesUnlocked}
              title={themesUnlocked ? 'Cambiar Theme' : 'Theme Bloqueado'}
            >
              <FontAwesomeIcon icon={faPalette} />
            </Button>
            {themesUnlocked && (
              <Form.Select
                size="sm"
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                style={{ width: '150px', marginLeft: '8px' }}
              >
                {themes.map(theme => (
                  <option key={theme.id} value={theme.id}>{theme.name}</option>
                ))}
              </Form.Select>
            )}
          </div>
        </div>

        <div className="mirc32-main">
          <div className="mirc32-channels-panel" style={{ width: channelPanelWidth }}>
            <div className="channels-header">
              CANALES {ircConnected && `(${displayChannels.length})`}
            </div>
            {displayChannels.map(ch => (
              <div
                key={ch.name}
                className={`channel-list-item ${mircActiveChannel === ch.name ? 'active' : ''}`}
                onClick={() => setMircActiveChannel(ch.name)}
              >
                <FontAwesomeIcon icon={faHashtag} className="me-2" />
                <span>{ch.name}</span>
                {ch.unread > 0 && <Badge bg="danger" className="ms-auto">{ch.unread}</Badge>}
              </div>
            ))}
            {!ircConnected && (
              <div className="channel-hint">
                <small>Conecta a LinkHub para ver canales reales</small>
              </div>
            )}
          </div>

          <div 
            className="mirc32-resizer" 
            onMouseDown={handleResizeStart}
          />

          <div className="mirc32-chat-area">
            <Nav variant="tabs" className="mirc-tabs">
              {displayChannels.map(ch => (
                <Nav.Item key={ch.name}>
                  <Nav.Link 
                    active={mircActiveChannel === ch.name}
                    onClick={() => setMircActiveChannel(ch.name)}
                  >
                    {ch.name}
                    {ch.unread > 0 && <Badge bg="danger" className="ms-1">{ch.unread}</Badge>}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>

            <div className="mirc32-chat" ref={mircChatRef}>
              {activeChannelMessages.length === 0 ? (
                <>
                  <div className="mirc32-message">
                    <span className="mirc-time">[{new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}]</span>
                    <span className="mirc-text" style={{ fontStyle: 'italic', color: '#888' }}>
                      {ircConnected 
                        ? `Conectado a ${mircActiveChannel}. Esperando mensajes...`
                        : `Modo demo. Conecta al servidor para ver mensajes reales.`
                      }
                    </span>
                  </div>
                  {!ircConnected && (
                    <>
                      <div className="mirc32-message">
                        <span className="mirc-time">[10:26:43]</span>
                        <span className="mirc-nick" style={{ color: '#009900' }}>@Anubis</span>
                        <span className="mirc-text">LinkHub Network</span>
                      </div>
                      <div className="mirc32-message mirc-event">
                        <span className="mirc-time">[10:27:15]</span>
                        <span className="mirc-text">* Conecta al servidor para ver actividad real</span>
                      </div>
                    </>
                  )}
                </>
              ) : (
                activeChannelMessages.map((msg, idx) => {
                  const time = new Date(msg.timestamp).toLocaleTimeString('es-ES', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    second: '2-digit' 
                  });
                  const nick = msg.from || msg.sender || 'System';
                  
                  return (
                    <div key={msg.id || idx} className="mirc32-message">
                      <span className="mirc-time">[{time}]</span>
                      <span className="mirc-nick" style={{ color: '#0066cc' }}>{nick}</span>
                      <span className="mirc-text">{msg.text}</span>
                    </div>
                  );
                })
              )}
              {/* ✅ Elemento invisible para auto-scroll */}
              <div ref={mircMessagesEndRef} style={{ height: 1 }} />
            </div>

            {/* ✅ Indicador flotante de nuevos mensajes */}
            {mircHasNewMessages && (
              <div 
                className="mirc32-new-messages-indicator"
                onClick={() => {
                  setMircHasNewMessages(false);
                  mircUserScrolledUp.current = false;
                  // Scroll manual del contenedor IRC (no afecta al documento global)
                  const chatContainer = mircChatRef.current;
                  if (chatContainer) {
                    chatContainer.scrollTop = chatContainer.scrollHeight;
                  }
                }}
              >
                ↓ Nuevos mensajes
              </div>
            )}

            <div className="mirc32-input">
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={ircConnected ? `Escribir en ${mircActiveChannel}...` : "Conecta al IRC primero!"}
                  value={commandInput}
                  onChange={(e) => setCommandInput(e.target.value)}
                  onKeyPress={async (e) => {
                    if (e.key === 'Enter') {
                      if (commandInput.startsWith('/')) {
                        handleCommand(commandInput);
                      } else if (ircConnected) {
                        addLog(`📤 Enviando a ${mircActiveChannel}: ${commandInput}`);
                        await linkHubIrcService.sendMessage(mircActiveChannel, commandInput);
                      }
                      setCommandInput('');
                    }
                  }}
                  style={{
                    background: mircDarkMode ? '#1e1e1e' : '#ffffff',
                    color: mircDarkMode ? '#d4d4d4' : '#000000',
                    fontFamily: 'Courier New, monospace',
                    border: `1px solid ${mircDarkMode ? '#444' : '#ccc'}`
                  }}
                />
                <Button 
                  variant={mircDarkMode ? 'dark' : 'light'} 
                  onClick={async () => {
                    if (commandInput.startsWith('/')) {
                      handleCommand(commandInput);
                    } else if (ircConnected) {
                      addLog(`📤 Enviando a ${mircActiveChannel}: ${commandInput}`);
                      await linkHubIrcService.sendMessage(mircActiveChannel, commandInput);
                    }
                    setCommandInput('');
                  }}
                >
                  Enviar
                </Button>
              </InputGroup>
              {/*<div className="mirc-hint">
                💡 Tip: {ircConnected 
                  ? `Conectado a ${ircConfig.server} - Escribe para chatear`
                  : 'Conecta a LinkHub'
                }
              </div> */}
            </div>
          </div>

          <div className="mirc32-users-panel">
            <div className="users-header">
              USUARIOS ({activeChannelUsers.length})
              {ircConnected && <FontAwesomeIcon icon={faCircle} className="ms-2" style={{ fontSize: '6px', color: '#34C759' }} />}
            </div>
            <div className="users-list-mirc">
              {activeChannelUsers.map(user => {
                const privInfo = getPrivilegeIcon(user.privilege);
                const prefix = user.privilege === 'owner' ? '~' : user.privilege === 'op' ? '@' : user.privilege === 'voice' ? '+' : '';
                const isMe = user.nick.toLowerCase() === ircConfig.nickname.toLowerCase();
                
                return (
                  <div
                    key={user.nick}
                    className={`user-list-item ${isMe ? 'user-list-item-me' : ''}`}
                    onContextMenu={(e) => handleUserRightClick(e, user)}
                    onDoubleClick={() => handleUserDoubleClick(user)}
                    title={isMe ? `${user.nick} (Tú)` : "Doble click para privado | Click derecho para opciones"}
                  >
                    <FontAwesomeIcon
                      icon={privInfo.icon}
                      style={{ color: isMe ? '#007AFF' : privInfo.color }}
                      className="me-2"
                    />
                    <span style={{ color: isMe ? '#007AFF' : 'inherit', fontWeight: isMe ? 700 : 'inherit' }}>
                      {prefix}{user.nick}
                      {isMe && <span className="mirc-me-badge">Tú</span>}
                    </span>
                    <Button 
                      variant="link" 
                      size="sm" 
                      className="ms-auto user-menu-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        handleUserRightClick({
                          preventDefault: () => {},
                          stopPropagation: () => {},
                          clientX: rect.left,
                          clientY: rect.bottom
                        } as any, user);
                      }}
                      title="Mostrar opciones"
                    >
                      <FontAwesomeIcon icon={faEllipsisV} />
                    </Button>
                  </div>
                );
              })}
              {!ircConnected && (
                <div className="channel-hint">
                  <small>Conecta a LinkHub para ver usuarios reales</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {contextMenu && (() => {
          const isTargetMe = contextMenu.user.nick.toLowerCase() === ircConfig.nickname.toLowerCase();
          
          return (
          <div
            className="context-menu-mirc"
            style={{ left: contextMenu.x, top: contextMenu.y, zIndex: 10000000 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="context-header">
              <FontAwesomeIcon icon={getPrivilegeIcon(contextMenu.user.privilege).icon} 
                style={{ color: getPrivilegeIcon(contextMenu.user.privilege).color }} 
                className="me-2" 
              />
              {contextMenu.user.nick}
              {isTargetMe && <span style={{ color: '#007AFF', fontSize: '11px', marginLeft: '6px' }}>(Tú)</span>}
            </div>
            <div className="context-divider" />
            
            {/* ✅ Whois - Siempre disponible */}
            <div className="context-item" onClick={() => handleUserAction('whois', contextMenu.user)}>
              <FontAwesomeIcon icon={faEye} className="me-2" />
              Whois
            </div>
            
            {/* ✅ Privado - Solo si NO soy yo */}
            {!isTargetMe ? (
              <div className="context-item" onClick={() => handleUserAction('query', contextMenu.user)}>
                <FontAwesomeIcon icon={faMessage} className="me-2" />
                Abrir Privado
              </div>
            ) : (
              <div className="context-item context-item-disabled">
                <FontAwesomeIcon icon={faUsers} className="me-2" />
                Este eres tú
              </div>
            )}
            
            {/* ✅ OPCIONES DE OPERADOR - Solo si soy @op o owner */}
            {amIOperator && !isTargetMe && (
              <>
                <div className="context-divider" />
                <div className="context-section-title">Privilegios</div>
                <div className="context-item" onClick={() => handleUserAction('op', contextMenu.user)}>
                  <FontAwesomeIcon icon={faUserShield} className="me-2" />
                  Dar OP
                </div>
                <div className="context-item" onClick={() => handleUserAction('deop', contextMenu.user)}>
                  <FontAwesomeIcon icon={faUserShield} className="me-2" style={{ opacity: 0.5 }} />
                  Quitar OP
                </div>
                <div className="context-item" onClick={() => handleUserAction('voice', contextMenu.user)}>
                  <FontAwesomeIcon icon={faCircle} className="me-2" />
                  Dar Voice
                </div>
                <div className="context-item" onClick={() => handleUserAction('devoice', contextMenu.user)}>
                  <FontAwesomeIcon icon={faCircle} className="me-2" style={{ opacity: 0.5 }} />
                  Quitar Voice
                </div>
                
                <div className="context-divider" />
                <div className="context-section-title">Moderación</div>
                <div className="context-item text-warning" onClick={() => handleUserAction('kick', contextMenu.user)}>
                  <FontAwesomeIcon icon={faTimes} className="me-2" />
                  Kick
                </div>
                <div className="context-item text-danger" onClick={() => handleUserAction('ban', contextMenu.user)}>
                  <FontAwesomeIcon icon={faBan} className="me-2" />
                  Ban
                </div>
                <div className="context-item text-warning" onClick={() => handleUserAction('unban', contextMenu.user)}>
                  <FontAwesomeIcon icon={faBan} className="me-2" style={{ opacity: 0.5 }} />
                  Unban
                </div>
                <div className="context-item text-danger" onClick={() => handleUserAction('kickban', contextMenu.user)}>
                  <FontAwesomeIcon icon={faBan} className="me-2" />
                  <FontAwesomeIcon icon={faTimes} className="me-2" style={{ marginLeft: '-8px' }} />
                  Kickban
                </div>
              </>
            )}
            
            {/* ✅ Mensaje si soy YO MISMO */}
            {amIOperator && isTargetMe && (
              <>
                <div className="context-divider" />
                <div className="context-item context-item-disabled">
                  <FontAwesomeIcon icon={faUsers} className="me-2" />
                  No puedes moderarte a ti mismo
                </div>
              </>
            )}
            
            {/* ✅ Mensaje si NO soy operador */}
            {!amIOperator && (
              <>
                <div className="context-divider" />
                <div className="context-item context-item-disabled">
                  <FontAwesomeIcon icon={faBan} className="me-2" />
                  Opciones de operador no disponibles
                </div>
              </>
            )}
          </div>
        );})()}
      </div>
    );
  };

  return (
    <div className="irc-manager">
      <style>{`
        /* Fix z-index para modales sobre windowmanager */
        .modal {
          z-index: 20000000 !important;
        }
        .modal-backdrop {
          z-index: 19999999 !important;
        }
        
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .connection-toast {
          animation: slideInRight 0.3s ease-out !important;
        }
      `}</style>
      
      {showConfetti && <Confetti />}

      {skillMessage && (
        <div 
          className="connection-toast"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            background: skillMessage.includes('Conectando') 
              ? 'linear-gradient(135deg, rgba(52, 120, 246, 0.95) 0%, rgba(48, 176, 199, 0.95) 100%)'
              : 'linear-gradient(135deg, rgba(52, 199, 89, 0.95) 0%, rgba(48, 176, 199, 0.95) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '12px 20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            maxWidth: '380px',
            animation: 'slideInRight 0.3s ease-out'
          }}
        >
          <div style={{
            background: 'rgba(255, 255, 255, 0.25)',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px'
          }}>
            {skillMessage.includes('Conectando') ? (
              <div style={{ 
                width: '16px', 
                height: '16px', 
                border: '2px solid #fff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <FontAwesomeIcon icon={faCheck} style={{ color: '#fff' }} />
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ 
              color: '#fff', 
              fontWeight: 600, 
              fontSize: '14px',
              marginBottom: skillMessage.includes('Conectado') ? '2px' : '0'
            }}>
              {skillMessage.replace('✅ ', '').replace('⏳ ', '')}
            </div>
            {skillMessage.includes('Conectado') && (
              <div style={{ 
                color: 'rgba(255, 255, 255, 0.85)', 
                fontSize: '12px',
                lineHeight: '1.3'
              }}>
                Servidor: {ircConfig.server}:{ircConfig.port}
                <br />
                Nick: {ircConfig.nickname}
              </div>
            )}
          </div>
          {!skillMessage.includes('Conectando') && (
            <button
              onClick={() => setSkillMessage('')}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '6px',
                width: '24px',
                height: '24px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '14px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      )}

      {/* Sistema de Notificaciones Toast */}
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 10000,
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        pointerEvents: 'none'
      }}>
        {notifications.map((notif, index) => {
          const colors = {
            success: { bg: 'rgba(52, 199, 89, 0.95)', icon: faCheck },
            error: { bg: 'rgba(255, 69, 58, 0.95)', icon: faTimes },
            warning: { bg: 'rgba(255, 159, 10, 0.95)', icon: faExclamationTriangle },
            info: { bg: 'rgba(52, 120, 246, 0.95)', icon: faInfoCircle }
          };
          
          const config = colors[notif.type] || colors.info;
          
          return (
            <div
              key={notif.id}
              style={{
                background: `linear-gradient(135deg, ${config.bg} 0%, rgba(48, 176, 199, 0.95) 100%)`,
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                padding: '12px 20px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                maxWidth: '380px',
                marginTop: index * 60 + 'px',
                animation: 'slideInRight 0.3s ease-out',
                pointerEvents: 'all'
              }}
            >
              <div style={{
                background: 'rgba(255, 255, 255, 0.25)',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                minWidth: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px'
              }}>
                <FontAwesomeIcon icon={config.icon} style={{ color: '#fff' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ 
                  color: '#fff', 
                  fontWeight: 600, 
                  fontSize: '14px',
                  marginBottom: notif.details ? '4px' : '0'
                }}>
                  {notif.message}
                </div>
                {notif.details && (
                  <div style={{ 
                    color: 'rgba(255, 255, 255, 0.85)', 
                    fontSize: '12px',
                    lineHeight: '1.4'
                  }}>
                    {notif.details}
                  </div>
                )}
              </div>
              <button
                onClick={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '6px',
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '12px',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="sidebar">
        <div 
          className={`menu-item ${activeSection === 'communities' ? 'active' : ''}`}
          onClick={() => setActiveSection('communities')}
        >
          <FontAwesomeIcon icon={faHashtag} className="menu-icon" />
          <span>Dashboard</span>
        </div>
        <div 
          className={`menu-item ${activeSection === 'users' ? 'active' : ''}`}
          onClick={() => setActiveSection('users')}
        >
          <FontAwesomeIcon icon={faUsers} className="menu-icon" />
          <span>Usuarios</span>
          <Badge bg="primary" className="menu-badge">{totalUsers}</Badge>
        </div>
        <div 
          className={`menu-item ${activeSection === 'channels' ? 'active' : ''}`}
          onClick={() => setActiveSection('channels')}
        >
          <FontAwesomeIcon icon={faComments} className="menu-icon" />
          <span>Comunidades</span>
          <Badge bg="primary" className="menu-badge">{ircChannels.length || 2}</Badge>
        </div>
        
        {mirc32Unlocked && (
          <>
            <div className="menu-divider" />
            <div 
              className={`menu-item menu-item-special ${activeSection === 'mirc32' ? 'active' : ''}`}
              onClick={() => setActiveSection('mirc32')}
            >
              <span className="menu-icon">🎮</span>
              <span>mIRC32</span>
              <Badge bg="danger" className="menu-badge" style={{ fontSize: '8px' }}>NEW!</Badge>
            </div>
          </>
        )}
      </div>

      <div className="main-content">
        {(activeSection === 'communities' || activeSection === 'mirc32') && (
          <div className="irc-status-bar" data-connected={ircConnected}>
            <div className="status-info">
              <div className="status-indicator" style={{ 
                background: ircConnected ? '#34C759' : '#8E8E93' 
              }}></div>
              <span className="status-text">
                {ircConnected ? (
                  <>
                    <strong>Conectado</strong> a {/*ircConfig.server}:{ircConfig.port*/} LinkHub Network como {ircConfig.nickname}
                  </>
                ) : (
                  <>
                    <strong>Desconectado</strong> - Red LinkHub no conectada
                  </>
                )}
              </span>
            </div>
            <div className="status-actions">
              {connectionLogs.length > 0 && (
                <Badge bg="info" className="me-2">
                  {connectionLogs.length} logs
                </Badge>
              )}
              {ircStats && (
                <Badge bg="secondary" className="me-2">
                  {ircStats.total_users} usuarios · {ircStats.active_channels} comunidades
                </Badge>
              )}
              {ircConnected ? (
                <Button variant="outline-danger" size="sm" onClick={handleIrcDisconnect}>
                  Desconectar
                </Button>
              ) : (
                <Button variant="outline-primary" size="sm" onClick={() => setShowIrcConfig(true)}>
                  Configurar IRC
                </Button>
              )}
            </div>
          </div>
        )}
        
        {activeSection === 'communities' && renderCommunities()}
        {activeSection === 'users' && renderUsers()}
        {activeSection === 'channels' && renderChannels()}
        {activeSection === 'mirc32' && renderMirc32()}
      </div>

      {windowManager.windows.map(win => {
        if (win.isMinimized) {
          return (
            <div
              key={win.id}
              className="chat-bubble-minimized"
              style={{
                position: 'fixed',
                left: win.position.x,
                top: win.position.y,
                width: win.size.width,
                height: win.size.height,
                zIndex: 100000 + win.zIndex  // ✅ Base alto para estar sobre todo el frontend
              }}
              onClick={() => windowManager.maximizeWindow(win.id)}
              title={`Click para restaurar: ${win.metadata?.channelName || 'Chat'}`}
            >
              <div className="bubble-content">
                <FontAwesomeIcon icon={faHashtag} />
                <span className="bubble-name">{win.metadata?.channelName || 'Chat'}</span>
              </div>
            </div>
          );
        }
        
        if (win.metadata?.channelName) {
          return (
            <ChatWindow
              key={win.id}
              window={win}
              channelName={win.metadata.channelName}
              onClose={() => windowManager.closeWindow(win.id)}
              onMinimize={() => windowManager.minimizeWindow(win.id)}
              onBringToFront={() => windowManager.bringToFront(win.id)}
            />
          );
        }
        return null;
      })}

      {/* Modal: Unirse a Canal */}
      <Modal show={showJoinChannelModal} onHide={() => {
        setShowJoinChannelModal(false);
        setJoinChannelInput('');
      }} centered>
        <Modal.Header 
          closeButton
          style={{
            background: 'linear-gradient(135deg, #30B0C7 0%, #0072FF 100%)',
            border: 'none',
            color: '#fff'
          }}
        >
          <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FontAwesomeIcon icon={faHashtag} />
            Unirse a Canal
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px' }}>
          <Form.Group>
            <Form.Label style={{ fontWeight: 600, marginBottom: '8px' }}>
              Nombre del canal
            </Form.Label>
            <InputGroup>
              <InputGroup.Text style={{
                background: 'linear-gradient(135deg, #30B0C7 0%, #0072FF 100%)',
                border: 'none',
                color: '#fff',
                fontWeight: 600
              }}>
                #
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="linkhub, general, ayuda..."
                value={joinChannelInput}
                onChange={(e) => setJoinChannelInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && joinChannelInput.trim()) {
                    handleJoinChannel(joinChannelInput.trim());
                    setShowJoinChannelModal(false);
                    setJoinChannelInput('');
                  }
                }}
                autoFocus
                style={{
                  border: '2px solid #e0e0e0',
                  borderLeft: 'none',
                  padding: '10px 16px'
                }}
              />
            </InputGroup>
            <Form.Text className="text-muted" style={{ marginTop: '8px', display: 'block' }}>
              💡 Puedes escribir el nombre con o sin el #. Por ejemplo: "linkhub" o "#linkhub"
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e0e0e0', padding: '16px 24px' }}>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowJoinChannelModal(false);
              setJoinChannelInput('');
            }}
            style={{ padding: '8px 20px' }}
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              if (joinChannelInput.trim()) {
                handleJoinChannel(joinChannelInput.trim());
                setShowJoinChannelModal(false);
                setJoinChannelInput('');
              }
            }}
            disabled={!joinChannelInput.trim()}
            style={{
              background: 'linear-gradient(135deg, #30B0C7 0%, #0072FF 100%)',
              border: 'none',
              padding: '8px 20px',
              fontWeight: 600
            }}
          >
            <FontAwesomeIcon icon={faCheck} className="me-2" />
            Unirse
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal: Acción Genérica (Kick, Ban, etc.) */}
      {actionModalConfig && (
        <Modal show={showActionModal} onHide={() => {
          setShowActionModal(false);
          setActionModalConfig(null);
        }} centered>
          <Modal.Header 
            closeButton
            style={{
              background: 'linear-gradient(135deg, #FF9500 0%, #FF6B35 100%)',
              border: 'none',
              color: '#fff'
            }}
          >
            <Modal.Title>{actionModalConfig.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '24px' }}>
            <Form.Group>
              <Form.Label style={{ fontWeight: 600, marginBottom: '8px' }}>
                {actionModalConfig.label}
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={actionModalConfig.placeholder}
                defaultValue={actionModalConfig.defaultValue}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    const value = (e.target as HTMLInputElement).value;
                    if (value.trim()) {
                      actionModalConfig.onConfirm(value.trim());
                      setShowActionModal(false);
                      setActionModalConfig(null);
                    }
                  }
                }}
                autoFocus
                style={{
                  border: '2px solid #e0e0e0',
                  padding: '10px 16px'
                }}
                id="action-modal-input"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #e0e0e0', padding: '16px 24px' }}>
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowActionModal(false);
                setActionModalConfig(null);
              }}
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              onClick={() => {
                const input = document.getElementById('action-modal-input') as HTMLInputElement;
                if (input && input.value.trim()) {
                  actionModalConfig.onConfirm(input.value.trim());
                  setShowActionModal(false);
                  setActionModalConfig(null);
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #FF9500 0%, #FF6B35 100%)',
                border: 'none',
                fontWeight: 600
              }}
            >
              Confirmar
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      <Modal show={showIrcConfig} onHide={() => setShowIrcConfig(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faServer} className="me-2" />
            Configuración de Conexión
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Servidor</Form.Label>
              <Form.Control
                type="text"
                value={ircConfig.server}
                onChange={(e) => setIrcConfig({...ircConfig, server: e.target.value})}
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Puerto</Form.Label>
                  <Form.Control
                    type="number"
                    value={ircConfig.port}
                    onChange={(e) => setIrcConfig({...ircConfig, port: parseInt(e.target.value)})}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>SSL</Form.Label>
                  <Form.Check
                    type="switch"
                    checked={ircConfig.useSSL}
                    onChange={(e) => setIrcConfig({...ircConfig, useSSL: e.target.checked})}
                    label={ircConfig.useSSL ? 'Habilitado' : 'Deshabilitado'}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Nickname</Form.Label>
              <Form.Control
                type="text"
                value={ircConfig.nickname}
                onChange={(e) => setIrcConfig({...ircConfig, nickname: e.target.value})}
              />
              <Form.Text className="text-muted">
                Alternativo: {ircConfig.alternativeNicks?.join(', ')}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Comunidades (separados por coma)</Form.Label>
              <Form.Control
                type="text"
                value={ircConfig.channels?.join(', ')}
                onChange={(e) => setIrcConfig({
                  ...ircConfig, 
                  channels: e.target.value.split(',').map(c => c.trim()).filter(c => c)
                })}
                placeholder="#linkhub, #general"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password (opcional)</Form.Label>
              <Form.Control
                type="password"
                value={ircConfig.password}
                onChange={(e) => setIrcConfig({...ircConfig, password: e.target.value})}
                placeholder="Dejar vacío si no es necesario"
              />
            </Form.Group>

            {connectionLogs.length > 0 && (
              <div className="connection-logs-panel">
                <div className="logs-header">
                  📋 Logs de Conexión
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => setConnectionLogs([])}
                    className="ms-2"
                  >
                    Limpiar
                  </Button>
                </div>
                <div className="logs-content">
                  {connectionLogs.map((log, idx) => (
                    <div key={idx} className="log-line">{log}</div>
                  ))}
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleTestBackend}>
            🧪 Test Backend
          </Button>
          <div style={{ flex: 1 }}></div>
          <Button variant="secondary" onClick={() => setShowIrcConfig(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleIrcConnect}>
            <FontAwesomeIcon icon={faServer} className="me-2" />
            Conectar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LinkHubNetworkTab;